import { latestExecutiveSummaryForDecision } from '../../lib/savedReportDecisionSource';
import { ScreeningSummaryView } from './summary/ScreeningSummaryView';

import React, { useState, useEffect, useRef } from 'react';
import { useStableCallback } from '../../hooks/useStableCallback';
import type { ReportTypeId } from '../../config/reportTypes';
import { useStore, serializeDraftBoundaryIdentity } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { eligibleDocIdsForSed } from '../../lib/evidenceDocEligibility';
import { selectReportLibraryReports } from '../../lib/reportVersionGroups';
import { GenerationErrorSourceLink } from './GenerationErrorSourceLink';
import { SentimentEvidenceSidebarNotices } from './SentimentEvidenceNotice';
import { TemplateOverrideProvider } from '../../lib/useEffectiveTemplate';
import { ThemeOverrideProvider } from '../../lib/useEffectiveReportTheme';
import { DEFAULT_THEME } from '../../types/reportTheme';
import {
  Search,
  MapPin,
  FileText,
  Download,
  Trash2,
  AlertTriangle,
  Info,
  Loader2,
  Plus,
  History,
  LayoutDashboard,
  Scale,
  BarChart3,
  Settings2,
  Database,
  ExternalLink,
  Key,
  Globe2,
  Waves,
  Zap as ZapIcon,
  Sun,
  TreePine,
  Wind,
  Upload,
  File,
  Copy,
  Users,
  Lock,
  Shield,
  Droplets,
  Building2,
  Cable,
  Leaf,
  FolderOpen,
  Menu as MenuIcon,
  PinOff,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { parseSiteAppPath, siteOverviewPath } from '../../lib/siteNavigation';
import { navigateBackToReports } from './reportBackNavigation';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { SiteSidebarActiveBoundary } from './SiteSidebarActiveBoundary';
import { Badge } from '../ui/badge';
import { SiteRagDashboard } from './SiteRagDashboard';
import { DashboardPage } from '../dashboard/DashboardPage';
import { PortfolioSiteListContext, PortfolioSiteListControls } from '../dashboard/PortfolioSiteListControls';
import { DEFAULT_SITE_VIEW, deriveSiteListMetadata, selectSiteList, buildSiteListOptions, type PortfolioSiteViewState } from '../../lib/portfolio/siteListView';
import { SiteRagReportView } from './SiteRagReportView';
import { resolveReportPresentationMode } from './reportPresentationMode';
import { SiteLandingPage } from './SiteLandingPage';
import { selectEngineRenderView } from './engineRenderView';
import { RouteLoadingSkeleton } from '../ui/RouteLoadingSkeleton';
import { readGuidePendingAction, clearGuidePendingAction } from '../reportGuide/deepLinkActions';
import { DeleteSiteDialog } from './DeleteSiteDialog';
// UXS-3 — `SiteCardReportRag` (FEPWIT 6-dot strip) and
// `pickDashboardEvidenceChip` (DATA GOOD / DATACRATE ENGINE pill)
// removed from the dashboard left-rail site card; the components stay
// available for the Overview tab's `ReportsByTypeCard` (a wider, less
// noisy surface) and for any future admin debug view.
// Per-section doc tagging now lives on the doc itself
// (`SiteDocument.sedSectionsHelpful`), surfaced through
// DocumentSelectionPanel + the wizard's `docDrawerOpen` overlay.
// sedBootstrap reads each doc's tags directly to decide which docs
// feed which sections; the wizard fires bootstrapSiteSed
// unconditionally after createSite.
import type { SiteEntity } from '../../types/site';
// ThermoAnalysisView integrated into Simulation section
import { APIProvider, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { toast } from 'sonner';
// The on-map boundary drawers (SiteBoundaryDrawer / MultiParcelDrawer) were
// retired from the create flow — drawing is deferred to the site's Map tab.
// The `BoundaryResult` shape is still the canonical boundary payload (KMZ
// import + the create payload both use it), so the type is kept.
import { type BoundaryResult } from './SiteBoundaryDrawer';
import { emptySiteContext } from '../../types/siteContext';
// DC-3 — wizard KMZ-import path joins the boundary-confirm cascade.
// Same EAS 4-stage sampler as the on-demand `refreshSiteElevation`
// path, so a wizard-imported KMZ on a small / coverage-edge polygon
// gets three more chances before the SED records "elevation
// unavailable". Persists `cascadeStages` onto BoundaryResult so the
// downstream siteEvidence stamping picks it up.
import { fetchPolygonElevationsCascading } from '../../lib/fetchPolygonElevationsCascading';
import { Map as MapIcon } from 'lucide-react';
import type { ProjectIntent } from '../../types/siteRag';
import { undecidedProjectIntent } from '../../lib/intent/undecidedProjectIntent';
import { PROVIDER_CATALOG, recommendProviders } from '../../services/dataProviders';
// ProjectIntentForm + SiteContextInput were the wizard's intent/brief steps —
// both are now captured on the site's own tabs after the identity-only create.

import { SiteBoundaryThumbnail, siteTotalAcres, formatSiteAcres } from './SiteBoundaryThumbnail';
import { siteHasUsableLocation } from '../../lib/siteBoundaryGeometry';
import { SiteCardLogo } from './SiteCardLogo';
import { resolveSiteCardBranding } from '../../lib/reportBranding';
import { useCompaniesBranding } from '../../lib/companyBranding';
import { resolveUploadedLogo } from '../../lib/resolveEffectiveLogo';
import { resolveActiveReportTemplate } from '../../lib/resolveActiveReportTemplate';
import type { CompanyArchetype } from '../../types/company';
// UXS-3 — REPORT_TYPE_REGISTRY / tierFromScore / tierLabel were used
// by the now-removed dashboard verdict chip ("→ Feasibility verdict ·
// Conditional"). The same information is surfaced one click in on the
// Site Landing header (UXS-2) and on the Reports tab list.
import { displayAddress } from '../../lib/siteAddressDisplay';
// Identity-only create modal — only `newDraftId` (mint a fresh id on open)
// and `removeDraft` (defensive stale-draft sweep on a successful create)
// remain in use; the multi-step draft snapshot/resume/autosave helpers were
// retired with the wizard.
import {
  newDraftId,
  removeDraft,
} from '../../services/wizardDraft';
import { useUrlSiteHydration, type EngineView, syncBrowserUrlForActiveSite } from './useUrlSiteHydration';
import { deriveSiteVerdict } from '../../lib/portfolio/deriveSiteVerdict';
import { VerdictDot } from '../shared/VerdictDot';
import { bandColorClass } from '../shared/verdictVisuals';
import { effectivePower, effectivePhases } from '../../lib/projectIntent';
// #81(a) source-scope chips (My / Company / Sub-company / All) REMOVED
// 2026-06-10 per William — the company filter row + verdict chips +
// data-scope selector below remain the site-list filters. The scope axis
// is now an implicit 'all' (no source filtering), so no site is hidden.
// #122 — dashboard DATA-SCOPE toggle (own / all / specific sub-company).
// Pure resolution lives in dashboardDataScope; the selector is a controlled
// tab strip rendered only when the user actually has sub-companies.
import { DashboardDataScopeSelector } from './DashboardDataScopeSelector';
import {
  type DashboardDataScope,
  resolveScopeCompanyIds,
  isEntityInScope,
  readPersistedScope,
  writePersistedScope,
  parseSpecificScope,
} from '../../lib/dashboardDataScope';
import { fetchSubtreeCompanies } from '../../services/companySubtreeQuery';
import { retryImport } from './docViewers/retryImport';
import { callAdminFunction } from '../admin/adminApi';
import { buildCompanyDisplayLabels, collectDescendantCompanyIds, hasSubCompanies } from '../../lib/companyHierarchy';
import { resolveAssignableCompanies, resolveAssignedCompanyId } from '../../lib/siteCompany';
import type { Company } from '../../types/company';
import { isIsolatedSyntheticFixtureMode, isSimulationE2eMode } from '../../lib/localDev/isLocalMockMode';



/** Short, human labels for the verdict/status quick-filter chips. */
const VERDICT_FILTER_LABELS: Record<'green' | 'amber' | 'orange' | 'red' | 'gray', string> = {
  green: 'Go',
  amber: 'Caution',
  orange: 'Conditional',
  red: 'No-go',
  gray: 'In progress',
};

/**
 * Self-healing stand-in for a report whose semantic body never hydrated.
 *
 * `fetchRagReports` fails CLOSED on a GCS body error — the catch at the body
 * fetch is silent and falls through to `markReportMetadataOnly`, so one
 * transient error left the report permanently `_metadataOnly` with nothing
 * anywhere retrying it. This surface was therefore a dead end twice over: it
 * told the user to "open it again" when nothing was loading, and its "Back to
 * reports" only cleared the store while the URL kept its `/reports/:id`
 * segment, so `useUrlSiteHydration` re-opened the very same report on the next
 * pass (the store-vs-URL bounce documented in `reportBackNavigation.ts`).
 *
 * It now retries the body fetch on mount and shows the wall ONLY when the
 * retry genuinely fails — with a retry that re-runs and a back that navigates.
 *
 * Rendered in the same branch as `SiteRagReportView`, which calls `useNavigate`
 * unconditionally, so router context is an assumption already shared here.
 */
export const ReportReaderRecovery = ({
  reportId,
  siteId,
}: {
  reportId: string;
  siteId: string | null;
}) => {
  const hydrateReportBody = useStore((s) => s.hydrateReportBody);
  const companyId = useStore((s) => s.companyId);
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const initialCompanyId = companyId;
    setFailed(false);
    void (async () => {
      const store = useStore.getState();
      if (!store.siteRag.reports.some((r) => r.id === reportId)) {
        try {
          await store.fetchRagReports();
        } catch {
          // fall through to hydrateReportBody
        }
      }
      if (cancelled || useStore.getState().companyId !== initialCompanyId) return;
      const afterFetch = useStore.getState();
      const matched = afterFetch.siteRag.reports.find((r) => r.id === reportId);
      if (matched && siteId && matched.siteId && matched.siteId !== siteId) {
        if (!cancelled && useStore.getState().companyId === initialCompanyId) {
          setFailed(true);
        }
        return;
      }
      if (matched && (!siteId || !matched.siteId || matched.siteId === siteId)) {
        const resolvedSiteId = matched.siteId ?? siteId;
        if (resolvedSiteId && afterFetch.siteRag.activeSiteId !== resolvedSiteId) {
          afterFetch.setActiveSite(resolvedSiteId);
        }
        if (afterFetch.siteRag.activeReportId !== reportId) {
          afterFetch.setActiveRagReport(reportId);
        }
      }
      const ok = await hydrateReportBody(reportId);
      // On success the store swap flips `reportPresentationMode` back to
      // 'continuous-html' and this component unmounts — only failure renders.
      if (!cancelled && useStore.getState().companyId === initialCompanyId && !ok) {
        setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [hydrateReportBody, reportId, siteId, companyId, attempt]);

  if (!failed) {
    return (
      <div
        role="status"
        aria-label="Loading report"
        className="flex h-full min-h-[32rem] w-full items-center justify-center bg-canvas p-6"
        data-testid="report-reader-hydrating"
      >
        <section className="w-full max-w-lg border border-border bg-surface-1 px-8 py-10 shadow-card">
          <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-full bg-info/10 text-info">
            <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
          </div>
          <p className="mb-2 type-meta font-semibold text-text-muted">Loading report</p>
          <h1 className="type-display leading-tight text-text-primary">Fetching the full report</h1>
          <p className="mt-4 type-body leading-6 text-text-secondary">
            This revision&apos;s semantic content wasn&apos;t loaded with the report list. Retrieving it now.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label="Report reader unavailable"
      className="flex h-full min-h-[32rem] w-full items-center justify-center bg-canvas p-6"
      data-testid="report-reader-unavailable"
    >
      <section className="w-full max-w-lg border border-border bg-surface-1 px-8 py-10 shadow-card">
        <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-full bg-info/10 text-info">
          <Shield aria-hidden="true" className="h-5 w-5" />
        </div>
        <p className="mb-2 type-meta font-semibold text-text-muted">Report body unavailable</p>
        <h1 className="type-display leading-tight text-text-primary">This report could not be loaded</h1>
        <p className="mt-4 type-body leading-6 text-text-secondary">
          The report list loaded, but this revision&apos;s full content could not be retrieved from
          storage. Retrying may resolve a transient failure; if it persists, re-run the report.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button
            className="min-h-11"
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            data-testid="report-reader-retry"
          >
            Retry
          </Button>
          <Button
            className="min-h-11"
            type="button"
            variant="outline"
            data-testid="report-reader-back"
            onClick={() => {
              const { setActiveSite, setActiveRagReport } = useStore.getState();
              // DESTINATION: the SITE's Analyses tab (`?tab=reports` — the
              // stable `reports` id now labels the live Analyses workspace), not
              // the global reports list. It keeps site context and is the
              // surface this panel's copy points at.
              //
              // Order matters, as in `reportBackNavigation.ts`: navigate FIRST
              // so react-router pushes the report-less URL synchronously, then
              // clear state — otherwise `useUrlSiteHydration` still reads a
              // `/reports/:id` path and re-opens this very report.
              //
              // Only the REPORT id is cleared. Keeping `activeSiteId` is what
              // demotes the derived engineView to 'site' (the site landing page
              // that owns the Analyses tab) instead of falling to 'dashboard'.
              if (siteId) {
                navigate(siteOverviewPath(siteId, 'reports'));
                setActiveRagReport(null);
                return;
              }
              // No resolvable site (cross-user / orphaned record) — the global
              // reports surface is the only safe destination.
              navigateBackToReports({ navigate, setActiveSite, setActiveRagReport });
            }}
          >
            Back to Analyses
          </Button>
        </div>
      </section>
    </div>
  );
};

/**
 * Memoised: App re-renders on simulation/canvas store writes this engine never
 * reads. Its three props are a view literal and two stable setters, so React
 * can skip the whole SiteRAG subtree on those renders.
 */
export function buildDraftCreateRequestKey(params: {
  nameKey: string;
  addrKey: string;
  lat: number;
  lng: number;
  assignedCompanyId?: string;
  duplicateSuffix?: string;
  siteBoundary?: Array<{ lat: number; lng: number }>;
  siteBoundaries?: Array<{ id?: string; label?: string; polygon: Array<{ lat: number; lng: number }> }>;
}): string {
  const boundaryKey = serializeDraftBoundaryIdentity({
    siteBoundary: params.siteBoundary,
    siteBoundaries: params.siteBoundaries,
  });
  return `draft:${params.nameKey}:${params.addrKey}:${params.lat.toFixed(6)}:${params.lng.toFixed(6)}:${boundaryKey}:${params.assignedCompanyId ?? ''}${params.duplicateSuffix ?? ''}`;
}

export interface DraftSiteCreateLifecycleOptions {
  currentUid: string | null;
  userCompanyId: string | null | undefined;
  onToastError?: (errMsg: string, err: unknown) => void;
}

export function useDraftSiteCreateLifecycle({
  currentUid,
  userCompanyId,
  onToastError,
}: DraftSiteCreateLifecycleOptions) {
  const [showNewAnalysis, setShowNewAnalysis] = useState(false);
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [boundaryResult, setBoundaryResult] = useState<BoundaryResult | null>(null);
  const [siteName, setSiteName] = useState('');
  const siteNameTouchedRef = useRef(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [kmzMode, setKmzMode] = useState<'draw' | 'import'>('draw');
  const [parsedKmz, setParsedKmz] = useState<import('../../services/kmzParser').ParsedKmz | null>(null);
  const [parsedSiteLayers, setParsedSiteLayers] = useState<import('../../types/site').SiteLayer[]>([]);
  const [layersReviewed, setLayersReviewed] = useState(false);
  const [isImportingKmz, setIsImportingKmz] = useState(false);
  const [draftId, setDraftId] = useState<string>('');
  const creatingSiteInFlightRef = useRef(false);
  const createSiteCommitStartedRef = useRef(false);
  const createSiteAbortControllerRef = useRef<AbortController | null>(null);
  const draftCreateRequestIdRef = useRef<string | null>(null);
  const activeCreateAttemptIdRef = useRef(0);
  const engineMountedRef = useRef(true);
  const onToastErrorRef = useRef(onToastError);
  onToastErrorRef.current = onToastError;

  useEffect(() => {
    engineMountedRef.current = true;
    return () => {
      engineMountedRef.current = false;
      activeCreateAttemptIdRef.current += 1;
      createSiteAbortControllerRef.current?.abort();
    };
  }, []);

  const previousScopeRef = useRef<{ uid: string | null; companyId: string | null } | null>(null);
  useEffect(() => {
    const normalizedCompany = userCompanyId ?? null;
    const prev = previousScopeRef.current;
    previousScopeRef.current = { uid: currentUid, companyId: normalizedCompany };
    if (!prev) return;
    const uidChanged = prev.uid !== null && currentUid !== prev.uid;
    const companyChanged = prev.companyId !== normalizedCompany;
    if (uidChanged || companyChanged) {
      activeCreateAttemptIdRef.current += 1;
      createSiteAbortControllerRef.current?.abort();
      createSiteAbortControllerRef.current = null;
      creatingSiteInFlightRef.current = false;
      createSiteCommitStartedRef.current = false;
      draftCreateRequestIdRef.current = null;
      setIsCreatingSite(false);
      setShowNewAnalysis(false);
    }
  }, [currentUid, userCompanyId]);

  /** Reset every create-modal slice back to its fresh-open defaults. */
  const resetWizardState = React.useCallback(() => {
    setSelectedPlace(null);
    setBoundaryResult(null);
    setSearchQuery('');
    setSiteName('');
    siteNameTouchedRef.current = false;
    setSelectedCompanyId('');
    setKmzMode('draw');
    setParsedKmz(null);
    setParsedSiteLayers([]);
    setLayersReviewed(false);
    setIsImportingKmz(false);
  }, []);

  const openWizard = React.useCallback(
    async (ensureCurrentUid?: () => Promise<void> | void) => {
      if (createSiteCommitStartedRef.current) return;
      createSiteAbortControllerRef.current?.abort();
      createSiteAbortControllerRef.current = null;
      setShowNewAnalysis(true);
      resetWizardState();

      if (ensureCurrentUid) {
        await ensureCurrentUid();
      }

      const nextDraftId = newDraftId();
      draftCreateRequestIdRef.current = nextDraftId;
      setDraftId(nextDraftId);
    },
    [resetWizardState],
  );

  const handleWizardClose = React.useCallback(() => {
    if (createSiteCommitStartedRef.current) {
      // Once setDoc enters the irreversible commit phase, block Escape/X/backdrop
      // dismissal until the write settles so a committed site is never silently abandoned.
      return;
    }
    activeCreateAttemptIdRef.current += 1;
    createSiteAbortControllerRef.current?.abort();
    createSiteAbortControllerRef.current = null;
    creatingSiteInFlightRef.current = false;
    createSiteCommitStartedRef.current = false;
    draftCreateRequestIdRef.current = null;
    setIsCreatingSite(false);
    setShowNewAnalysis(false);
  }, []);

  const beginCreateAttempt = React.useCallback(() => {
    if (creatingSiteInFlightRef.current || isCreatingSite) return null;
    const attemptId = activeCreateAttemptIdRef.current + 1;
    activeCreateAttemptIdRef.current = attemptId;
    creatingSiteInFlightRef.current = true;
    createSiteCommitStartedRef.current = false;
    const abortController = new AbortController();
    createSiteAbortControllerRef.current = abortController;
    let attemptCommitStarted = false;
    const isCurrentAttempt = () => activeCreateAttemptIdRef.current === attemptId;
    const markCommitStarted = () => {
      attemptCommitStarted = true;
      if (isCurrentAttempt()) {
        createSiteCommitStartedRef.current = true;
      }
    };
    const hasCommitStarted = () => attemptCommitStarted;
    const markCreatingSpinner = () => {
      if (isCurrentAttempt() && engineMountedRef.current) {
        setIsCreatingSite(true);
      }
    };
    const cleanupAttempt = () => {
      if (isCurrentAttempt()) {
        creatingSiteInFlightRef.current = false;
        createSiteCommitStartedRef.current = false;
        if (createSiteAbortControllerRef.current === abortController) {
          createSiteAbortControllerRef.current = null;
        }
        if (engineMountedRef.current) {
          setIsCreatingSite(false);
        }
      }
    };
    return {
      attemptId,
      abortController,
      isCurrentAttempt,
      markCommitStarted,
      hasCommitStarted,
      markCreatingSpinner,
      cleanupAttempt,
    };
  }, [isCreatingSite]);

  const executeDraftCreateAttempt = React.useCallback(
    async (params: {
      confirmBeforeCreate?: (attempt: NonNullable<ReturnType<typeof beginCreateAttempt>>) => Promise<boolean>;
      getInitiatingScope: () =>
        | Promise<{ uid: string | null; companyId: string | null }>
        | { uid: string | null; companyId: string | null };
      getLiveScope: () => { uid: string | null; companyId: string | null };
      invokeCreateSite: (ctx: {
        attempt: NonNullable<ReturnType<typeof beginCreateAttempt>>;
        abortSignal: AbortSignal;
        onCommitStart: () => void;
      }) => Promise<string | null>;
      onCommittedSuccess: (
        siteId: string,
        scope: { uid: string | null; companyId: string | null },
      ) => void;
    }) => {
      const attempt = beginCreateAttempt();
      if (!attempt) return;
      const {
        abortController,
        isCurrentAttempt,
        markCommitStarted,
        hasCommitStarted,
        markCreatingSpinner,
        cleanupAttempt,
      } = attempt;

      let initiatingAuthUid: string | null = null;
      let initiatingCompanyScope: string | null = null;
      try {
        if (params.confirmBeforeCreate) {
          const ok = await params.confirmBeforeCreate(attempt);
          if (!ok || !isCurrentAttempt() || abortController.signal.aborted) return;
        }

        if (!isCurrentAttempt() || abortController.signal.aborted) return;
        markCreatingSpinner();

        const initScope = await params.getInitiatingScope();
        initiatingAuthUid = initScope.uid;
        initiatingCompanyScope = initScope.companyId;
        if (!isCurrentAttempt() || abortController.signal.aborted) return;

        const siteId = await params.invokeCreateSite({
          attempt,
          abortSignal: abortController.signal,
          onCommitStart: () => {
            markCommitStarted();
          },
        });
        if (!siteId) return;

        if (!engineMountedRef.current || !isCurrentAttempt()) return;
        if (abortController.signal.aborted && !hasCommitStarted()) return;
        const { uid: postAuthUid, companyId: postCompanyScope } = params.getLiveScope();
        if (postAuthUid !== initiatingAuthUid || postCompanyScope !== initiatingCompanyScope) {
          return;
        }

        draftCreateRequestIdRef.current = null;
        setShowNewAnalysis(false);
        params.onCommittedSuccess(siteId, { uid: postAuthUid, companyId: postCompanyScope });
      } catch (e) {
        if (!engineMountedRef.current || !isCurrentAttempt()) {
          return;
        }
        const errMsg = (e as Error)?.message ?? 'unknown error';
        if (
          (abortController.signal.aborted && !hasCommitStarted()) ||
          (e as Error)?.name === 'AbortError' ||
          /auth or company scope changed/i.test(errMsg)
        ) {
          return;
        }
        const { uid: postAuthUid, companyId: postCompanyScope } = params.getLiveScope();
        if (postAuthUid !== initiatingAuthUid || postCompanyScope !== initiatingCompanyScope) {
          return;
        }
        console.error('[wizard] createSite failed:', e);
        if (onToastErrorRef.current) {
          onToastErrorRef.current(errMsg, e);
        } else {
          toast.error(`Failed to create site: ${errMsg}`);
        }
      } finally {
        cleanupAttempt();
      }
    },
    [beginCreateAttempt],
  );

  return {
    showNewAnalysis,
    setShowNewAnalysis,
    isCreatingSite,
    setIsCreatingSite,
    searchQuery,
    setSearchQuery,
    selectedPlace,
    setSelectedPlace,
    boundaryResult,
    setBoundaryResult,
    siteName,
    setSiteName,
    siteNameTouchedRef,
    selectedCompanyId,
    setSelectedCompanyId,
    kmzMode,
    setKmzMode,
    parsedKmz,
    setParsedKmz,
    parsedSiteLayers,
    setParsedSiteLayers,
    layersReviewed,
    setLayersReviewed,
    isImportingKmz,
    setIsImportingKmz,
    draftId,
    setDraftId,
    creatingSiteInFlightRef,
    createSiteCommitStartedRef,
    createSiteAbortControllerRef,
    draftCreateRequestIdRef,
    activeCreateAttemptIdRef,
    engineMountedRef,
    resetWizardState,
    openWizard,
    handleWizardClose,
    beginCreateAttempt,
    executeDraftCreateAttempt,
  };
}

export const SiteRagEngine = React.memo(function SiteRagEngine({
  engineView,
  setEngineView,
  useDashboardV2 = false,
}: {
  engineView: EngineView;
  setEngineView: (v: EngineView) => void;
  useDashboardV2?: boolean;
}) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // The browser release gate needs to exercise report citation retirement with
  // the offline report fixture. It deliberately has no Maps/provider key. The
  // report path is map-safe without a provider (map content already has a
  // keyless fallback), while all production builds eliminate this DEV-only arm.
  if (isSimulationE2eMode() || isIsolatedSyntheticFixtureMode()) {
    return <SiteRagEngineContent engineView={engineView} setEngineView={setEngineView} useDashboardV2={useDashboardV2} />;
  }

  if (!apiKey) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background p-6 text-center font-mono">
        <div className="max-w-md space-y-4">
          <AlertTriangle className="w-12 h-12 text-warning mx-auto" />
          <h2 className="text-lg font-bold uppercase tracking-widest">API Key Required</h2>
          <p className="text-xs text-text-secondary uppercase tracking-wider leading-relaxed">
            SiteRAG requires a Google Maps API Key to fetch location data and geocode addresses.
          </p>
        </div>
      </div>
    );
  }

  // Maps JS version UNPINNED (weekly channel) — the 3.64 pin existed because
  // 3.65 removed google.maps.drawing.DrawingManager, which the boundary /
  // multi-parcel drawers used. Both drawers now draw via the manager-free
  // click-to-add-vertex pattern (SiteLayerDrawOverlay et al.), and nothing in
  // src/ touches google.maps.drawing.*, so the deprecated 'drawing' library
  // is no longer requested either.
  return (
    <APIProvider apiKey={apiKey} libraries={['places', 'geometry']}>
      <SiteRagEngineContent engineView={engineView} setEngineView={setEngineView} useDashboardV2={useDashboardV2} />
    </APIProvider>
  );
});

const SiteRagEngineContent = ({
  engineView,
  setEngineView,
  useDashboardV2,
}: {
  engineView: EngineView;
  setEngineView: (v: EngineView) => void;
  useDashboardV2: boolean;
}) => {
  // Per-slice selectors — Fix_React_Error_185_Prompt step 3.
  // Bare `useStore()` re-renders on any store mutation (including
  // every site's evidenceReadiness write); selectors keep this
  // component subscribed only to what it actually reads.
  const siteRag = useStore(
    useShallow((s) => ({
      sites: s.siteRag.sites,
      activeSiteId: s.siteRag.activeSiteId,
      reports: s.siteRag.reports,
      activeReportId: s.siteRag.activeReportId,
      isLoading: s.siteRag.isLoading,
      error: s.siteRag.error,
      isSitesLoaded: s.siteRag.isSitesLoaded,
    })),
  );
  const userApiKeys = useStore(s => s.userApiKeys);
  const setActiveRagReport = useStore(s => s.setActiveRagReport);
  const deleteRagReport = useStore(s => s.deleteRagReport);
  const setViewMode = useStore(s => s.setViewMode);
  const toggleDemoMode = useStore(s => s.toggleDemoMode);
  const setUserApiKeys = useStore(s => s.setUserApiKeys);
  const setSubtreeCompanies = useStore(s => s.setSubtreeCompanies);
  const setActiveSite = useStore(s => s.setActiveSite);
  const portfolioComposites = useStore(s => s.portfolio.composites);
  const portfolioAnalysisContexts = useStore(s => s.portfolio.analysisContexts);
  const decisionBandConfig = useStore(s => s.portfolio.bandConfig);
  const deleteSite = useStore(s => s.deleteSite);
  const createSite = useStore(s => s.createSite);
  const updateSite = useStore(s => s.updateSite);
  const userRole = useStore(s => s.userRole);
  const userCompanyId = useStore(s => s.companyId);
  const companyName = useStore(s => s.companyName);
  // STBW-1 — read the store's reportTheme so we can wrap
  // <SiteRagReportView> in <ThemeOverrideProvider>. Routing the store
  // theme through the Provider is a no-op today (useEffectiveReportTheme
  // already falls through to the store) but pins the wrap-site so future
  // per-report overrides attach here without further refactor.
  //
  // TSC-3 (2026-05-18) — prefer the transient Studio draft channel
  // `activeDraftTheme` when present. Open Studio → change colours →
  // navigate to a site report → the report renders with the IN-FLIGHT
  // (unsaved) draft, no Save click required. Save commits to
  // `reportTheme` and clears `activeDraftTheme` in the same `set` call
  // (see store/index.ts:commitReportTheme) so the next render falls
  // through to the persisted value with no visual flicker.
  const storeTheme = useStore(s => s.reportTheme);
  const draftTheme = useStore(s => s.activeDraftTheme);
  const effectiveTheme = draftTheme ?? storeTheme ?? DEFAULT_THEME;
  // TPN-1 (2026-05-18) — same propagation pattern as the theme draft
  // channel, but for the TEMPLATE. When the Template Studio is open
  // in another tab/window, `activeDraftTemplate` carries the in-flight
  // section/start-mode/pagePin edits; wrapping the viewer in
  // TemplateOverrideProvider lets useEffectiveTemplate() pick up the
  // draft instead of the persisted Zustand row. Null means "no Studio
  // override" — the viewer falls through to its store-derived template.
  const draftTemplate = useStore(s => s.activeDraftTemplate);
  // SITE-CARD-LOGOS — to show the ASSIGNED template's brand (company name
  // + logo) on dashboard site cards (matching the report cards + covers),
  // resolve the same session active/default template, honouring a Studio
  // draft override. Sites carry no per-site templateId, so resolution runs
  // through active/default → archetype seed per site (archetype varies).
  const reportTemplates = useStore(s => s.reportTemplates) ?? [];
  const activeTemplateId = useStore(s => s.activeTemplateId) ?? null;
  const companyArchetype = useStore(s => s.companyArchetype) ?? null;
  // Brand logo (feat/org-logo) — effective uploaded logo (user → org) fed
  // into each card's resolveSiteCardBranding `effectiveLogoUrl`.
  const brandUserLogoUrl = useStore(s => s.userLogoUrl);
  const brandOrgLogoUrl = useStore(s => s.orgLogoUrl);
  const cardEffectiveLogoUrl = React.useMemo(
    () => resolveUploadedLogo({ userLogoUrl: brandUserLogoUrl, orgLogoUrl: brandOrgLogoUrl }),
    [brandUserLogoUrl, brandOrgLogoUrl],
  );
  // OWNING-COMPANY brand (2026-06-10) — the site card's primary mark is the
  // brand of the site's OWNING company (`site.companyId`), never the
  // viewer's session/theme/template brand. Resolve the distinct owning
  // companyIds of the listed sites once (session-cached) and feed each
  // card's `resolveSiteCardBranding` via `owningCompanyBranding`. Lookups
  // that are rules-denied (cross-tenant non-admin) resolve to nulls, so
  // those cards keep the v1.3.2 ownership-guard fallback.
  const siteOwningCompanyIds = React.useMemo(
    () => siteRag.sites.map((s) => s.companyId),
    [siteRag.sites],
  );
  const owningCompanyBrandingById = useCompaniesBranding(siteOwningCompanyIds);

  const navigate = useNavigate();

  // W22-1 — Hydrate engineView + activeSiteId from the URL path on
  // mount AND on popstate. Without this, a cold load of
  // /sites/{id}?tab=overview keeps engineView === 'dashboard' and
  // SiteLandingPage never mounts (live-prod walkthrough finding,
  // 2026-05-19). The hook is idempotent — it only writes when state
  // DIFFERS from the URL, so in-app navigation is unaffected. The
  // sites-not-loaded-yet race is handled via the dep array on
  // `siteRag.sites` (the effect re-runs once fetchSites populates).
  useUrlSiteHydration({
    sites: siteRag.sites,
    reports: siteRag.reports,
    activeSiteId: siteRag.activeSiteId,
    activeReportId: siteRag.activeReportId,
    engineView,
    setActiveSite,
    setActiveRagReport,
    setEngineView,
  });

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  // Address-Coordinate Reconciliation AC2 — reverse-geocode of the most
  // recent boundary centroid (drawn or KMZ-imported). The boundary is
  // canonical; whenever it changes we recompute the address. Null until
  // the first reverse-geocode lands (or it returns null on failure — in
  // which case we fall back to the typed selectedPlace address at create
  // time, but `geocodingProvenance` stays unset).
  const [derivedAddress, setDerivedAddress] = useState<{
    formattedAddress: string;
    locality: string | null;
  } | null>(null);
  // 2026-05-09: nuclearInterest used to default to 'considering' which
  // silently authored a hybrid power-source preference for the user via
  // intentToPreferences. Default both fields to 'undecided' so we only
  // emit downstream opinions the user actually picked.
  //
  // DRAFT-ONLY CREATE — the modal does not collect project intent, load, or
  // timeline at creation time. Seed with `undecidedProjectIntent()` so
  // workloadProfile, powerRequirement, timeline, priority, and all other
  // intent fields remain explicitly unknown ('user-unknown' / 'undecided')
  // across UI, storage, and downstream readers until the user enters them.
  const [projectIntent] = useState<ProjectIntent>(() => undecidedProjectIntent());
  // Site_Context SC5 — freeform "what we know about this site" prose. The
  // identity-only create modal no longer collects this (it's captured on the
  // site's own tabs post-create), so it stays empty here; the createSite
  // payload's `siteContext` seed remains wired but inert. Retained so the
  // seed path can be re-enabled without re-plumbing.
  const [siteContextText] = useState('');
  // After the wizard creates a site we hand off to SiteLandingPage with this
  // initial tab so the empty-state CTA is the first thing the user sees.
  // Cleared on the next site change.
  // `landingInitialTab` overrides the SiteLandingPage's per-site sticky tab
  // memory. 'home' is the explicit reset (used on dashboard site-card
  // click — the Aurora Atrium Home is now the single landing); 'reports' is
  // what the rerun-completed flow sets so the user sees their new report in
  // the list; 'documents' is the wizard's first-time-user landing. (The
  // Path to GO landing is retired — new and coordinate-first sites land on
  // Home, Path to GO removal stage 2.)
  const [landingInitialTab, setLandingInitialTab] = useState<'home' | 'reports' | 'documents' | null>(null);
  const [currentUid, setCurrentUid] = useState<string | null>(null);
  const {
    showNewAnalysis,
    setShowNewAnalysis,
    isCreatingSite,
    searchQuery,
    setSearchQuery,
    selectedPlace,
    setSelectedPlace,
    boundaryResult,
    setBoundaryResult,
    siteName,
    setSiteName,
    siteNameTouchedRef,
    selectedCompanyId,
    setSelectedCompanyId,
    kmzMode,
    setKmzMode,
    parsedKmz,
    setParsedKmz,
    parsedSiteLayers,
    setParsedSiteLayers,
    layersReviewed,
    setLayersReviewed,
    isImportingKmz,
    setIsImportingKmz,
    draftId,
    creatingSiteInFlightRef,
    draftCreateRequestIdRef,
    openWizard: openDraftCreateWizard,
    handleWizardClose,
    executeDraftCreateAttempt,
  } = useDraftSiteCreateLifecycle({ currentUid, userCompanyId });
  const [siteToDelete, setSiteToDelete] = useState<SiteEntity | null>(null);
  const [reportSearch, setReportSearch] = useState('');
  const [companyFilterId, setCompanyFilterId] = useState<string>('all');
  const [companyFilterOptions, setCompanyFilterOptions] = useState<Company[]>([]);
  // Part B — verdict/status quick-filter for the site list. Useful for a
  // company viewing its own reports (the common no-sub-org case where the
  // org filter is empty and pointless). 'all' = no verdict scoping.
  const [verdictFilter, setVerdictFilter] = useState<'all' | 'green' | 'amber' | 'orange' | 'red' | 'gray'>('all');
  // #122 — dashboard DATA-SCOPE (own / all / specific sub-company). Narrows
  // the COMPANY axis of the site + report lists for parent-org users; ANDs
  // with the source/verdict/company filters. Defaults to 'all' (pre-#122
  // behaviour). `dataScopeSubtree` is the user's own org + descendants,
  // enumerated directly from Firestore (authorized for ANY user in the tree,
  // not just admins — see companySubtreeQuery). Empty for leaf/solo users, in
  // which case the selector stays hidden and the scope is inert.
  const [dataScope, setDataScope] = useState<DashboardDataScope>('all');
  const [dataScopeSubtree, setDataScopeSubtree] = useState<Company[]>([]);
  const [reportSort] = useState<'date' | 'score' | 'name' | 'readiness'>('date');
  const [siteListView, setSiteListView] = useState<PortfolioSiteViewState>(DEFAULT_SITE_VIEW);
  const isApiLoaded = useApiIsLoaded();

  /**
   * Open the create-site MODAL (2026-06-10 identity-only redesign).
   * Delegates to the shared `useDraftSiteCreateLifecycle` production controller.
   */
  const openWizard = async () => {
    await openDraftCreateWizard(async () => {
      if (!currentUid) {
        try {
          const { auth } = await import('../../firebase');
          setCurrentUid(auth.currentUser?.uid ?? null);
        } catch {
          /* uid stays null — company stamping falls back to the user's own org */
        }
      }
    });
  };

  // Listen for "New Site" trigger from File menu
  useEffect(() => {
    const handler = () => { void openWizard(); };
    window.addEventListener('datacrate:new-site', handler);
    return () => window.removeEventListener('datacrate:new-site', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUid]);

  // #122 — resolve the signed-in uid on mount (independent of the wizard) so
  // the per-user data-scope preference round-trips on first paint. Reuses the
  // existing `currentUid` slot that openWizard also populates.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { auth } = await import('../../firebase');
      const uid = auth.currentUser?.uid ?? null;
      if (!cancelled && uid) setCurrentUid((prev) => prev ?? uid);
    })();
    return () => { cancelled = true; };
  }, []);

  // #122 — load the user's company SUBTREE (own org + descendants) directly
  // from Firestore. Authorized for ANY user in the tree by the
  // `companies where path array-contains self` rule arm — not just admins —
  // so a non-admin parent-org user can detect + pick among their sub-orgs.
  // Degrades to [] (selector hidden) for solo/leaf users or on any error.
  useEffect(() => {
    if (typeof userCompanyId !== 'string' || userCompanyId.trim().length === 0) {
      setDataScopeSubtree([]);
      // Mirror into the shared store so the Dashboard's descendant-aware
      // portfolio scope resets too (solo/leaf user → no subtree).
      setSubtreeCompanies([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const rows = await fetchSubtreeCompanies(userCompanyId);
      if (!cancelled) {
        setDataScopeSubtree(rows);
        // Share the SAME verified subtree with the Dashboard (and any other
        // descendant-aware surface) so it resolves the visible company set via
        // collectDescendantCompanyIds rather than an exact companyId match.
        setSubtreeCompanies(rows);
      }
    })();
    return () => { cancelled = true; };
  }, [userCompanyId, setSubtreeCompanies]);

  // #122 — restore the persisted scope once we know the uid. Defaults to 'all'
  // (pre-#122 behaviour) when nothing is stored. Runs when the uid resolves.
  useEffect(() => {
    if (!currentUid) return;
    setDataScope(readPersistedScope(currentUid));
  }, [currentUid]);

  // Report Quality Guide — open new-site wizard when returning from /guide/reports.
  useEffect(() => {
    const pending = readGuidePendingAction();
    if (pending?.target.kind === 'new-site') {
      clearGuidePendingAction();
      void openWizard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Drop the active draft from disk — call after a successful site creation.
   * The modal no longer autosaves, so this is normally a no-op, but it stays
   * defensive: a stale draft persisted by an older build (or the File-menu
   * entry point) is cleaned up on a successful create.
   */
  const clearActiveDraft = () => {
    if (currentUid && draftId) removeDraft(currentUid, draftId);
  };

  // #122 — the DESCENDANT sub-companies (own org excluded) offered as
  // individual data-scope chips. Derived from the directly-enumerated subtree.
  // Empty for leaf/solo users → the scope selector stays hidden entirely.
  const dataScopeSubCompanies = React.useMemo(() => {
    const own = typeof userCompanyId === 'string' ? userCompanyId : null;
    if (!own) return [] as Array<{ id: string; label: string }>;
    const labels = buildCompanyDisplayLabels(dataScopeSubtree);
    return dataScopeSubtree
      .filter((c) => c.id !== own)
      .map((c) => ({ id: c.id, label: labels.get(c.id) ?? c.name }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [dataScopeSubtree, userCompanyId]);

  // #122 — the authorized subtree id set (own + descendants), used to resolve
  // the scope into a company-id set. This is the SAME verified universe the
  // fetch hops loaded; the scope only ever SUBSETS it (never widens).
  const dataScopeSubtreeIds = React.useMemo(() => {
    const ids = dataScopeSubtree.map((c) => c.id);
    if (typeof userCompanyId === 'string' && userCompanyId.trim().length > 0) ids.push(userCompanyId);
    return ids;
  }, [dataScopeSubtree, userCompanyId]);

  // #122 — resolve the active scope into the set of company ids to show. For
  // 'all' (or when the user has no sub-companies) this is the full subtree and
  // the downstream predicate is a passthrough.
  const scopeCompanyIds = React.useMemo(
    () => resolveScopeCompanyIds(dataScope, userCompanyId, dataScopeSubtreeIds),
    [dataScope, userCompanyId, dataScopeSubtreeIds],
  );

  // #122 — the scope axis is meaningful only when the user actually has
  // sub-companies. With none, the selector is hidden and the scope is inert
  // (we never apply the company-id filter — see the site/report filter chains).
  const showDataScope = dataScopeSubCompanies.length > 0;

  // #122 — whether the company-axis scope filter should actively narrow. True
  // only for a sub-company user who has picked 'own' or a specific sub-org;
  // 'all' (and the no-subtree case) is a passthrough.
  const dataScopeActive = showDataScope && dataScope !== 'all';

  // Group reports by version chain — show only latest version, others accessible via dropdown
  const { filteredReports, versionGroups } = React.useMemo(() => {
    // #122 — apply the data-scope (company-axis) narrowing to the report list
    // FIRST so grouping/version-collapse operate only over in-scope reports. A
    // pure subset of the already authorized subtree; 'all'/no-subtree is a
    // passthrough. Companyless reports belong to the user → in scope for
    // 'own'/'all', excluded for a specific sub-company view.
    const scopedReports = dataScopeActive
      ? siteRag.reports.filter((r) => isEntityInScope(r.companyId, scopeCompanyIds, userCompanyId))
      : siteRag.reports;

    return selectReportLibraryReports(scopedReports, reportSearch, reportSort);
  }, [siteRag.reports, reportSearch, reportSort, dataScopeActive, scopeCompanyIds, userCompanyId]);

  const verdictBySiteId = React.useMemo(() => {
    const next = new Map<string, ReturnType<typeof deriveSiteVerdict>>();
    for (const site of siteRag.sites) {
      const siteReps = siteRag.reports.filter(r => (site.reportIds ?? []).includes(r.id) && (!r.siteId || r.siteId === site.id));
      const latestExecutiveSummaryReport = latestExecutiveSummaryForDecision(siteReps);
      next.set(
        site.id,
        deriveSiteVerdict(
          portfolioComposites[site.id] ?? null,
          latestExecutiveSummaryReport?.overallStatus ?? null,
          decisionBandConfig ?? undefined,
        ),
      );
    }
    return next;
  }, [siteRag.sites, siteRag.reports, portfolioComposites, decisionBandConfig]);

  // Part B — verdict colours actually present across the user's sites, in a
  // fixed go→no-go order. Drives the adaptive verdict quick-filter so we
  // never render a chip for a status that has zero matching sites.
  const availableVerdictColors = React.useMemo(() => {
    const present = new Set<string>();
    verdictBySiteId.forEach((v) => present.add(v?.bandColor ?? 'gray'));
    const order: Array<'green' | 'amber' | 'orange' | 'red' | 'gray'> = ['green', 'amber', 'orange', 'red', 'gray'];
    return order.filter((c) => present.has(c));
  }, [verdictBySiteId]);

  useEffect(() => {
    if (userRole !== 'admin') {
      setCompanyFilterOptions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await callAdminFunction<{ companies: Company[] }>('listCompanies');
        if (!cancelled) {
          setCompanyFilterOptions(response.companies ?? []);
        }
      } catch {
        if (!cancelled) setCompanyFilterOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userRole]);

  const siteCompanyIds = React.useMemo(() => {
    const ids = new Set<string>();
    siteRag.sites.forEach((site) => {
      if (typeof site.companyId === 'string' && site.companyId.trim().length > 0) ids.add(site.companyId);
    });
    return ids;
  }, [siteRag.sites]);

  const companyCards = React.useMemo(() => {
    const known = new Map<string, Company>();
    companyFilterOptions.forEach((company) => known.set(company.id, company));
    const rows: Company[] = [];
    siteCompanyIds.forEach((companyId) => {
      rows.push(known.get(companyId) ?? {
        id: companyId,
        name: companyId,
        parentCompanyId: null,
        features: { siteRag: true, simulation: true },
        settings: { defaultRole: 'viewer', approvalRequired: false },
        createdAt: '',
        createdBy: '',
      });
    });
    return rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [companyFilterOptions, siteCompanyIds]);

  const effectiveCompanyFilterIds = React.useMemo(() => {
    if (companyFilterId === 'all') return null;
    const universe = companyFilterOptions.length > 0 ? companyFilterOptions : companyCards;
    return collectDescendantCompanyIds(universe, companyFilterId);
  }, [companyCards, companyFilterId, companyFilterOptions]);

  // The left-rail site filter chain (search + company hierarchy + verdict +
  // data-scope) before the shared portfolio selector. Both views consume the
  // same selected and ordered rows; these organization controls retain their
  // existing authorization scope.
  const scopedSidebarSites = React.useMemo(() => {
    let sites = [...siteRag.sites];
    if (reportSearch) {
      const q = reportSearch.toLowerCase();
      sites = sites.filter(s => (s.name ?? '').toLowerCase().includes(q) || displayAddress(s).toLowerCase().includes(q));
    }
    if (effectiveCompanyFilterIds && effectiveCompanyFilterIds.size > 0) {
      sites = sites.filter((site) =>
        typeof site.companyId === 'string' && effectiveCompanyFilterIds.has(site.companyId),
      );
    }
    if (verdictFilter !== 'all') {
      sites = sites.filter(
        (site) => (verdictBySiteId.get(site.id)?.bandColor ?? 'gray') === verdictFilter,
      );
    }
    if (dataScopeActive) {
      sites = sites.filter((site) =>
        isEntityInScope(site.companyId, scopeCompanyIds, userCompanyId),
      );
    }
    return sites;
  }, [siteRag.sites, reportSearch, effectiveCompanyFilterIds, verdictFilter, verdictBySiteId, dataScopeActive, scopeCompanyIds, userCompanyId]);

  const siteListMetadata = React.useMemo(() => scopedSidebarSites.map(site => deriveSiteListMetadata(
    site, siteRag.reports, portfolioComposites[site.id] ?? null, portfolioAnalysisContexts?.[site.id],
  )), [scopedSidebarSites, siteRag.reports, portfolioComposites, portfolioAnalysisContexts]);
  const siteListOptions = React.useMemo(() => buildSiteListOptions(siteListMetadata), [siteListMetadata]);
  const selectedSiteList = React.useMemo(() => selectSiteList(siteListMetadata, siteListView), [siteListMetadata, siteListView]);
  const filteredSidebarSites = React.useMemo(() => selectedSiteList.map(item => item.site), [selectedSiteList]);
  const siteListContext = React.useMemo(() => ({
    state: siteListView,
    onChange: setSiteListView,
    items: selectedSiteList,
    options: siteListOptions,
    totalCount: siteRag.sites.length,
    search: reportSearch,
    onSearch: setReportSearch,
    scopeFiltersActive: verdictFilter !== 'all' || companyFilterId !== 'all' || dataScopeActive,
    onReset: () => {
      setSiteListView(DEFAULT_SITE_VIEW);
      setReportSearch('');
      setVerdictFilter('all');
      setCompanyFilterId('all');
      setDataScope('all');
      writePersistedScope(currentUid, 'all');
    },
  }), [siteListView, selectedSiteList, siteListOptions, siteRag.sites.length, reportSearch, verdictFilter, companyFilterId, dataScopeActive, currentUid]);

  // The shared selector also supplies the dashboard's exact visible set.
  const dashboardVisibleSiteIds = React.useMemo<ReadonlySet<string> | null>(() => {
    return new Set(filteredSidebarSites.map(s => s.id));
  }, [filteredSidebarSites]);

  // Part B — adaptive filter set. An org/sub-org filter is only meaningful
  // when the user's tree actually has sub-orgs (an admin viewing the whole
  // estate, or a company whose own company has children). For the common
  // case — a single company with no sub-companies — the company buttons are
  // empty noise, so we hide them and surface the verdict/status filter
  // instead. Admins keep the company filter whenever >1 company is in view.
  const showCompanyFilter = React.useMemo(() => {
    if (userRole === 'admin') return companyCards.length > 1;
    if (typeof userCompanyId !== 'string' || userCompanyId.trim().length === 0) return false;
    const universe = companyFilterOptions.length > 0 ? companyFilterOptions : companyCards;
    return hasSubCompanies(universe, userCompanyId);
  }, [companyCards, companyFilterOptions, userCompanyId, userRole]);

  // New-Site company picker — the orgs the user may assign a new site to:
  // their OWN company plus its descendant sub-orgs ONLY (never a parent or
  // sibling — no cross-tenant widening). Mirrors the descendant universe the
  // filter already uses (companyFilterOptions for admins, companyCards for
  // non-admins). Returns [] when the user has no companyId, so the picker
  // simply does not render and the site assigns to the user's own org.
  const assignableCompanies = React.useMemo<Company[]>(() => {
    const universe = companyFilterOptions.length > 0 ? companyFilterOptions : companyCards;
    return resolveAssignableCompanies(universe, userCompanyId, companyName);
  }, [companyCards, companyFilterOptions, companyName, userCompanyId]);

  const assignableCompanyLabels = React.useMemo(
    () => buildCompanyDisplayLabels(assignableCompanies),
    [assignableCompanies],
  );

  // Show the picker only when there's a genuine choice: the user's company
  // has sub-orgs AND more than one assignable org is in scope.
  const showCompanyPicker = assignableCompanies.length > 1;

  // Create-site MODAL gate — name (required) AND a location (a picked place
  // OR an imported KMZ boundary). Drawing a boundary is deferred to the Map
  // tab, so the modal never requires a polygon at creation.
  const canCreateSite =
    siteName.trim().length > 0 &&
    (Boolean(selectedPlace?.geometry?.location) || Boolean(boundaryResult));

  // #122 — change handler: update state + persist per-user (survives reload).
  const handleDataScopeChange = React.useCallback(
    (next: DashboardDataScope) => {
      setDataScope(next);
      writePersistedScope(currentUid, next);
    },
    [currentUid],
  );

  // #122 — reconcile a stale 'specific:<id>' pin whose sub-company is no longer
  // in the subtree (org removed / re-parented). Fall back to 'all' so the user
  // never gets stuck on an empty, unselectable scope. Only runs once the
  // subtree has loaded (a non-empty list) to avoid clobbering during fetch.
  useEffect(() => {
    if (dataScopeSubtree.length === 0) return;
    const specificId = parseSpecificScope(dataScope);
    if (!specificId) return;
    const stillPresent = dataScopeSubCompanies.some((c) => c.id === specificId);
    if (!stillPresent) handleDataScopeChange('all');
  }, [dataScope, dataScopeSubCompanies, dataScopeSubtree.length, handleDataScopeChange]);

  useEffect(() => {
    if (companyFilterId === 'all') return;
    const exists = companyCards.some((company) => company.id === companyFilterId);
    if (!exists) setCompanyFilterId('all');
  }, [companyCards, companyFilterId]);

  // Clear a verdict scope that no longer matches any visible site.
  useEffect(() => {
    if (verdictFilter !== 'all' && !availableVerdictColors.includes(verdictFilter)) {
      setVerdictFilter('all');
    }
  }, [availableVerdictColors, verdictFilter]);

  // Non-admin default scope. Only pin the filter to the user's own org when
  // the legacy company filter is actually rendered (i.e. the org has sub-orgs
  // AND the hierarchy-aware DashboardDataScopeSelector is NOT shown) — that
  // default then includes the org *and its sub-orgs* via descendant
  // inclusion in effectiveCompanyFilterIds. With NO sub-orgs the org filter
  // is hidden and pinning a single companyId would wrongly hide the user's
  // not-yet-company-shared personal sites (companyId empty), so we stay on
  // 'all'. Likewise when showDataScope owns the company axis (parent-org user
  // with sub-companies) the legacy chips are suppressed, so this effect must
  // stay inert and leave companyFilterId on the 'all' passthrough — otherwise
  // an invisible control would silently (double-)narrow the site list against
  // the data-scope filter. Admins manage scope manually and are never
  // auto-pinned.
  useEffect(() => {
    if (userRole === 'admin') return;
    if (!showCompanyFilter || showDataScope) {
      setCompanyFilterId('all');
      return;
    }
    if (typeof userCompanyId === 'string' && userCompanyId.trim().length > 0) {
      setCompanyFilterId(userCompanyId);
    }
  }, [userCompanyId, userRole, showCompanyFilter, showDataScope]);

  // Clear the one-shot landing tab once we're no longer rendering the
  // freshly-created site, so the next visit to a different site starts on
  // its normal Overview default.
  useEffect(() => {
    if (landingInitialTab && siteRag.activeSiteId == null) {
      setLandingInitialTab(null);
    }
  }, [landingInitialTab, siteRag.activeSiteId]);

  // Rerun-completed bridge — when FeasibilityRerunPanel closes its diff
  // dialog, it dispatches this event so we land the user on the site's
  // Reports tab instead of the new report viewer. Clearing
  // activeReportId here is defensive (the panel already does it); pinning
  // activeSiteId guarantees the engine view flips back to 'site'.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ siteId?: string }>).detail;
      if (!detail?.siteId) return;
      const target = siteOverviewPath(detail.siteId, 'reports');
      try {
        window.history.replaceState({}, '', target);
        navigate(target, { replace: true });
      } catch {
        // best-effort URL sync
      }
      setLandingInitialTab('reports');
      setActiveSite(detail.siteId);
      useStore.getState().setActiveRagReport(null);
    };
    window.addEventListener('datacrate:rerun-completed', handler);
    return () => window.removeEventListener('datacrate:rerun-completed', handler);
  }, [navigate, setActiveSite]);

  // Open a site on its Home tab (set active site, switch to the site view,
  // clear the active report, and strip a stale ?tab=). Every site lands on
  // Home — including new and coordinate-first sites, whose Home journey band
  // names the screening action their coordinates unlock (Path to GO removal,
  // stage 2: those used to land on the retired Path to GO tab).
  const openSiteOverview = React.useCallback((siteId: string) => {
    setActiveSite(siteId);
    setEngineView('site');
    setActiveRagReport(null);
    // Explicit `?tab=` deep links are resolved by SiteLandingPage before this
    // prop and are unaffected.
    setLandingInitialTab('home');
    try {
      const url = new URL(window.location.href);
      url.pathname = siteOverviewPath(siteId);
      url.searchParams.delete('tab');
      // A stale `phase` / `guided` (retired Path to GO params) must not ride
      // a site-card open into another site.
      url.searchParams.delete('phase');
      url.searchParams.delete('guided');
      window.history.replaceState({}, '', url.toString());
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
    } catch {
      // best-effort URL cleanup only
    }
  }, [navigate, setActiveSite, setEngineView, setActiveRagReport, setLandingInitialTab]);

  const activeReport = siteRag.reports.find(r => r.id === siteRag.activeReportId);
  const reportPresentationMode = activeReport
    ? resolveReportPresentationMode(activeReport, false)
    : null;
  const activeSite = siteRag.sites.find(s => s.id === siteRag.activeSiteId);

  // Stable identities for the memoised <SiteLandingPage> (React.memo): the
  // inline closures that used to sit on its JSX were new props on every engine
  // render, which re-rendered the whole 7k-line page each time. Each wrapper
  // keeps one identity and always runs the latest closure.
  const handleViewReport = useStableCallback((reportId: string) => setActiveRagReport(reportId));
  const handleGenerateReport = useStableCallback(() => { void openWizard(); });
  const handleGenerateTypedReport = useStableCallback(async (typeId: ReportTypeId) => {
    if (!activeSite) return;
    try {
      await useStore.getState().generateTypedReport(activeSite.id, typeId);
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  });
  const handleSiteDeleted = useStableCallback(() => {
    setActiveSite(null);
    setEngineView('dashboard');
  });

  // Task #61 — single source of truth for which main pane renders. Keeps the
  // `engineView === 'report'`-without-a-report transient from flashing the
  // "Select or Generate a Report" placeholder during a site-card switch.
  const engineRenderView = selectEngineRenderView({
    engineView,
    hasActiveSite: Boolean(activeSite),
    hasActiveReport: Boolean(activeReport),
  });

  const handleDuplicateReport = async (report: import('../../types/siteRag').SiteRagReport) => {
    try {
      if (report.reportRevision) {
        toast.error(
          'Validated report revisions cannot be copied. Use “Add another” to create an independent lineage.',
        );
        return;
      }
      const { auth, db } = await import('../../firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      const user = auth.currentUser;
      if (!user) return;

      const newId = `rag-${Date.now()}`;
      const duplicate = {
        ...JSON.parse(JSON.stringify(report)),
        id: newId,
        siteName: `${report.siteName} (Copy)`,
        createdAt: new Date().toISOString(),
        sedId: undefined, // Don't link to original SED
      };

      await setDoc(doc(db, `users/${user.uid}/reports`, newId), duplicate);
      useStore.setState((state) => ({
        siteRag: {
          ...state.siteRag,
          reports: [duplicate, ...state.siteRag.reports].slice(0, 50),
        },
      }));
      toast.success(`Duplicated "${report.siteName}"`);
    } catch (e) {
      toast.error(`Failed to duplicate: ${(e as Error).message}`);
    }
  };

  useEffect(() => {
    // NAV-LIST — `activeReport` is the source of truth for the report view.
    // The DASHBOARD report cards open a report by setting `activeReportId`
    // WITHOUT a URL navigation (see SiteRagDashboard), so this effect must
    // still promote to the report view from there. The auto-jump bug (a stale
    // `activeReportId` from loadReports/setActiveSite snapping the SITE landing
    // into a report) is fixed authoritatively in useUrlSiteHydration: when the
    // path is a site-landing / reports-list URL (no `/reports/:id`) it clears
    // `activeReportId` AND demotes `engineView` back to 'site'. We intentionally
    // do NOT URL-gate here to avoid breaking the no-navigate dashboard flow.
    if (activeReport) {
      setEngineView('report');
    } else if (siteRag.activeSiteId) {
      setEngineView('site');
    } else if (engineView === 'report' || engineView === 'site') {
      setEngineView('dashboard');
    }
  }, [activeReport, siteRag.activeSiteId, siteRag.sites]);

  /**
   * Address-Coordinate Reconciliation AC2 — shared helper used by both
   * boundary surfaces in the wizard (drawn + KMZ-imported). Reverse-
   * geocodes the centroid; stores the result in `derivedAddress` so the
   * create-site call uses the boundary-derived address; surfaces a drift
   * toast when the typed search and the centroid disagree by >25 km.
   *
   * Fire-and-forget. A reverse-geocode failure leaves `derivedAddress`
   * null; the create path falls back to the typed `selectedPlace`
   * address but DOES NOT stamp `geocodingProvenance: 'reverse-geocode'`,
   * so the AC3 chip stays hidden on those rare cases.
   */
  const revGeocodeAndMaybeWarn = async (
    centroid: { lat: number; lng: number },
    typed: google.maps.places.PlaceResult | null,
  ): Promise<void> => {
    try {
      const { reverseGeocodeLatLng, addressBoundaryDriftMeters } =
        await import('../../services/geocoding');
      const rev = await reverseGeocodeLatLng(centroid.lat, centroid.lng);
      if (!rev) return;
      setDerivedAddress({ formattedAddress: rev.formattedAddress, locality: rev.locality });
      // Drift detection — only the typed-address geocode matters here;
      // a typed selectedPlace already carries the geocoded lat/lng from
      // Google's Places SDK, so no extra API call is needed.
      const typedLoc = typed?.geometry?.location;
      if (!typedLoc) return;
      const typedPoint = { lat: typedLoc.lat(), lng: typedLoc.lng() };
      const driftM = addressBoundaryDriftMeters(typedPoint, centroid);
      if (driftM !== null && driftM > 25_000) {
        const km = Math.round(driftM / 1000);
        const label = rev.locality ?? rev.formattedAddress;
        // toast.info shows a neutral non-blocking note; this isn't an error.
        toast.message(
          `Address updated to "${label}" — derived from your drawn boundary. ` +
          `Your typed address resolved ~${km} km away.`,
        );
      }
    } catch (e) {
      console.warn('[AC2] reverse-geocode failed:', (e as Error)?.message);
    }
  };

  /**
   * Site-only creation (3-step wizard finale). Validates the location + name,
   * then writes a SiteEntity with no reports and routes the user to the new
   * site's landing page with the Reports tab open. The old multi-step
   * "Generate Report" flow is gone — reports are picked from the site page.
   */
  const handleCreateSiteOnly = async () => {
    if (creatingSiteInFlightRef.current || isCreatingSite) return;
    // IDENTITY-ONLY CREATE — the location requirement is satisfied by a
    // picked place OR an imported boundary (either supplies a centroid).
    // Previously a `selectedPlace` was mandatory; the modal now allows a
    // KMZ-only create with no typed search, so accept either source.
    const placeLoc = selectedPlace?.geometry?.location ?? null;
    if (!placeLoc && !boundaryResult) {
      toast.error('Select a location from search, or import a KMZ boundary.');
      return;
    }

    const lat = boundaryResult ? boundaryResult.centroid.lat : placeLoc!.lat();
    const lng = boundaryResult ? boundaryResult.centroid.lng : placeLoc!.lng();
    // Address-Coordinate Reconciliation AC2 — when a boundary is drawn /
    // imported, the boundary-derived address (reverse-geocode of the
    // centroid) wins over the typed search. The typed address was a hint
    // to help find the parcel; once the user picked the actual polygon,
    // the polygon's coordinates supersede the typed address forever.
    //
    // If the reverse-geocode failed (network, ZERO_RESULTS, etc.) we
    // fall back to the typed address but DO NOT stamp the provenance —
    // legacy unknown is the right state for the chip to stay hidden.
    const hasBoundary = !!boundaryResult;
    const boundaryDerivedAddress = hasBoundary ? derivedAddress?.formattedAddress ?? null : null;
    const typedAddress = selectedPlace?.formatted_address || selectedPlace?.name || 'Unknown Address';
    // Boundary-only create with no reverse-geocode yet → fall back to the
    // typed address (if any) or a neutral placeholder; the Map tab can
    // refine the address later.
    const address = boundaryDerivedAddress ?? typedAddress;
    const geocodingProvenance: 'reverse-geocode' | undefined =
      hasBoundary && boundaryDerivedAddress ? 'reverse-geocode' : undefined;
    const addressUpdatedAt = geocodingProvenance ? new Date().toISOString() : undefined;
    const resolvedSiteName = siteName.trim() || selectedPlace?.name || 'Site ' + new Date().toLocaleDateString();

    if (!resolvedSiteName.trim()) {
      toast.error('Site name is required.');
      return;
    }
    if (resolvedSiteName.length > 100) {
      toast.error('Site name must be under 100 characters.');
      return;
    }
    if (!isFinite(lat) || lat < -90 || lat > 90 || !isFinite(lng) || lng < -180 || lng > 180) {
      toast.error('Invalid coordinates. Please select a valid location.');
      return;
    }
    if (Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01) {
      toast.error('Invalid location (0, 0). Please select a specific address.');
      return;
    }

    // EL2: BoundaryResult.elevation{Min,Max,Avg} is now `number | null`.
    // When the sample failed (all null), persist `undefined` on the
    // site so downstream consumers see "no elevation data" rather than
    // a synthesised `{ min: 0, max: 0, avg: 0 }` triple that legacy
    // pre-EL2 code wrote and that this pack exists to eliminate.
    // Units: metres ASL.
    const eMin = boundaryResult?.elevationMin;
    const eMax = boundaryResult?.elevationMax;
    const eAvg = boundaryResult?.elevationAvg;
    const allElevFinite =
      typeof eMin === 'number' && Number.isFinite(eMin) &&
      typeof eMax === 'number' && Number.isFinite(eMax) &&
      typeof eAvg === 'number' && Number.isFinite(eAvg);
    // EL3 — thread stdev + pointsSampled through to the persisted site
    // so the SED compile path can populate elevationStdev /
    // elevationPointsSampled on SiteProfileEvidence.
    // DC-3 — also thread the cascade audit trail when it's present
    // (boundary-draw + KMZ-import paths both populate it post-DC-3);
    // siteEvidence reads `siteElevationRange.cascadeStages` and stamps
    // `attemptStages` on the siteProfile section so the PRE chip
    // tooltip can surface the strategies the platform tried.
    const elevationRange = allElevFinite
      ? {
          min: eMin,
          max: eMax,
          avg: eAvg,
          stdev: boundaryResult?.elevationStdev ?? null,
          pointsSampled: boundaryResult?.elevationPointsSampled,
          ...(boundaryResult?.sourceQualification ? { sourceQualification: boundaryResult.sourceQualification } : {}),
          ...(boundaryResult?.cascadeSource ? { cascadeSource: boundaryResult.cascadeSource } : {}),
          ...(boundaryResult?.cascadeStages && boundaryResult.cascadeStages.length > 0
            ? { cascadeStages: boundaryResult.cascadeStages }
            : {}),
        }
      : undefined;

    // Duplicate-site guard (Reader Polish + R-131 get-or-create follow-up). Catch
    // both a same-NAME collision AND a same-PARCEL collision — same address, or a
    // centroid within ~35 m. The parcel match closes the create-time dup gap where
    // the wizard would otherwise mint a SECOND doc for one real parcel under a
    // slightly different name (the name-only guard missed it). Soft confirm only —
    // NEVER a hard block: list the matches so the user can pick a different
    // name/parcel or knowingly proceed.
    const normLower = (v?: string) => (v ?? '').trim().toLowerCase();
    const COORD_EPS = 0.0003; // ~33 m at mid-latitudes — same parcel, not same town
    const nameKey = normLower(resolvedSiteName);
    const addrKey = normLower(address);
    const allSites = useStore.getState().siteRag.sites;
    const dupById = new Map<string, (typeof allSites)[number]>();
    for (const s of allSites) {
      const sameName = normLower(s.name) === nameKey;
      const sameAddr = addrKey.length > 0 && normLower(s.address) === addrKey;
      const loc = s.location;
      const sameParcel = !!loc && Math.abs(loc.lat - lat) < COORD_EPS && Math.abs(loc.lng - lng) < COORD_EPS;
      if (sameName || sameAddr || sameParcel) dupById.set(s.id, s);
    }
    const existingDuplicates = [...dupById.values()];

    let resolvedAuthRef: typeof import('../../firebase')['auth'] | null = null;
    await executeDraftCreateAttempt({
      confirmBeforeCreate:
        existingDuplicates.length > 0
          ? async () => {
              const { openConfirmDialog } = await import('../ui/confirmDialog');
              return openConfirmDialog({
                title: 'Possible duplicate site',
                confirmLabel: 'Create anyway',
                cancelLabel: 'Go back',
                body: (
                  <>
                    <p>
                      You already have {existingDuplicates.length} site{existingDuplicates.length === 1 ? '' : 's'} with the same name or at this location:
                    </p>
                    <ul className="list-disc pl-4 space-y-0.5 text-text-primary">
                      {existingDuplicates.slice(0, 5).map(s => (
                        <li key={s.id}>
                          <span className="font-bold">{s.name}</span>
                          <span className="text-text-muted"> · {s.address || 'No address'} · created {new Date(s.createdAt).toLocaleDateString()}</span>
                        </li>
                      ))}
                      {existingDuplicates.length > 5 && (
                        <li className="text-text-muted">+{existingDuplicates.length - 5} more</li>
                      )}
                    </ul>
                    <p>Create another site here, or go back to adjust the name or location?</p>
                  </>
                ),
              });
            }
          : undefined,
      getInitiatingScope: async () => {
        const { auth } = await import('../../firebase');
        resolvedAuthRef = auth;
        return {
          uid: auth.currentUser?.uid ?? null,
          companyId: useStore.getState().companyId ?? null,
        };
      },
      getLiveScope: () => ({
        uid: resolvedAuthRef?.currentUser?.uid ?? null,
        companyId: useStore.getState().companyId ?? null,
      }),
      invokeCreateSite: async ({ attempt, abortSignal, onCommitStart }) => {
        const { isCurrentAttempt, markCommitStarted } = attempt;
        const { createSite: cs } = useStore.getState();
        const seededContextText = siteContextText.trim();
        const multiParcelPayload =
          boundaryResult?.parcels && boundaryResult.parcels.length > 0
            ? await (async () => {
                const { computeAggregate } = await import('../../lib/siteBoundaryGeometry');
                const aggregate = computeAggregate(boundaryResult.parcels!);
                return {
                  siteBoundaries: boundaryResult.parcels,
                  siteBoundaryAggregate: aggregate,
                  siteBoundary: boundaryResult.parcels![0].polygon,
                  siteArea: aggregate.totalArea,
                  sitePerimeter: aggregate.totalPerimeter,
                  siteCentroid: aggregate.aggregateCentroid,
                };
              })()
            : {};
        if (!isCurrentAttempt() || abortSignal.aborted) return null;
        const assignedCompanyId = resolveAssignedCompanyId(selectedCompanyId, assignableCompanies);
        const duplicateSuffix = existingDuplicates.length > 0
          ? `:dup-${existingDuplicates.length}-${draftCreateRequestIdRef.current || draftId || 'explicit'}`
          : '';
        const draftRequestId = buildDraftCreateRequestKey({
          nameKey,
          addrKey,
          lat,
          lng,
          assignedCompanyId,
          duplicateSuffix,
          siteBoundary: boundaryResult?.boundary,
          siteBoundaries: boundaryResult?.parcels,
        });
        const siteId = await cs({
          name: resolvedSiteName,
          companyId: assignedCompanyId,
          address,
          location: { lat, lng },
          ...(boundaryResult ? { studyArea: { schemaVersion: 1 as const, name: 'DC + BTM development area', revision: 1, included: [], roles: [], updatedAt: new Date().toISOString() } } : {}),
          siteBoundary: boundaryResult?.boundary,
          siteArea: boundaryResult?.areaAcres,
          sitePerimeter: boundaryResult?.perimeterKm,
          siteCentroid: boundaryResult?.centroid,
          ...multiParcelPayload,
          siteElevationRange: elevationRange,
          siteLayers: parsedSiteLayers.length > 0 ? parsedSiteLayers : undefined,
          projectIntent: projectIntent,
          draftOnly: true,
          draftRequestId,
          abortSignal: abortSignal,
          onCommitStart: () => {
            markCommitStarted();
            onCommitStart();
          },
          geocodingProvenance,
          addressUpdatedAt,
          siteContext: seededContextText.length > 0
            ? {
              ...emptySiteContext(),
              text: seededContextText,
              updatedAt: new Date().toISOString(),
            }
            : undefined,
        });
        const createdSite = useStore.getState().siteRag.sites.find(s => s.id === siteId);
        if (!createdSite) return null;
        return siteId;
      },
      onCommittedSuccess: (siteId) => {
        clearActiveDraft();
        openSiteOverview(siteId);
        toast.success('Draft site saved. Add project intent, upload documents, or start research on the site page when ready.');
      },
    });
  };

  // NOTE: `handleBoundaryConfirm` (the on-map draw confirmation) was retired
  // with the create-site wizard — drawing a boundary is now deferred to the
  // site's Map tab. KMZ import (`handleKmzFile` below) remains the only
  // boundary-at-creation path and still routes its centroid through the same
  // reverse-geocode (`revGeocodeAndMaybeWarn`) + cascade-elevation plumbing.

  /**
   * KMZ import path. Promotes the top-scoring boundary candidate into the existing
   * BoundaryResult shape so the rest of the pipeline stays uniform. KMZ doesn't
   * carry elevation, so we sample it from the Google Elevation API (vertices +
   * centroid) the same way the boundary drawer does — falling back to 0 only if
   * that best-effort lookup fails (handleGenerate already tolerates a zeroed
   * elevationRange). Previously these were hard-coded to 0, which is why every
   * KMZ-imported site reported "Elevation 0m / Range 0–0m".
   */
  const handleKmzFile = async (file: File) => {
    setIsImportingKmz(true);
    try {
      const { parseKmzOrKml } = await (await import('../../utils/safeImport')).safeImport(() => import('../../services/kmzParser'));
      const parsed = await parseKmzOrKml(file);
      for (const w of parsed.warnings) toast.message(w);
      if (parsed.boundaries.length === 0) {
        toast.error('No boundary polygon found in the KMZ. Try drawing instead.');
        setIsImportingKmz(false);
        return;
      }
      const top = parsed.boundaries[0];
      const perimeterKm = ringPerimeterKm(top.coordinates);
      // DC-3 — route the wizard KMZ-import elevation sample through
      // the EAS 4-stage cascade (vertex → in-polygon-grid →
      // buffered-bbox-grid → USGS centroid). Stage 1 reproduces the
      // legacy single-shot behaviour; the additional stages catch
      // polygons that previously fell straight through to "elevation
      // unavailable".
      //
      // EL2: `null` propagates as `null` (not `0`). When every stage
      // fails the SED-assembly + render layer say "Data unavailable"
      // rather than synthesising "0 m ASL". Units: metres ASL.
      // EL3: also threads stdev + pointsSampled so AI prompts can flag
      // a degraded sample (3 of 5 returned).
      const cascade = await fetchPolygonElevationsCascading(top.coordinates);
      const elev = cascade.kind === 'success' ? cascade.stats : null;
      const cascadeStages = cascade.kind === 'success'
        ? cascade.stats.stages
        : cascade.meta.stages;
      const cascadeSource = cascade.kind === 'success' ? cascade.stats.source : undefined;

      let parcels: import('../../types/siteBoundary').SiteBoundaryParcel[] | undefined;
      let areaAcres = top.areaAcres;
      let totalPerimeterKm = perimeterKm;
      let centroid = top.centroid;
      if (parsed.boundaries.length > 1 || (top.holes && top.holes.length > 0)) {
        const { makeParcel, computeAggregate, parcelPerimeter } = await import('../../lib/siteBoundaryGeometry');
        parcels = parsed.boundaries.map((cand, i) =>
          makeParcel(cand.coordinates, {
            id: `p${i + 1}`,
            label: cand.name ?? `Parcel ${i + 1}`,
            ...(cand.holes && cand.holes.length > 0 ? { holes: cand.holes } : {}),
            area: cand.areaAcres,
            perimeter: cand.holes && cand.holes.length > 0
              ? parcelPerimeter(cand.coordinates, cand.holes)
              : ringPerimeterKm(cand.coordinates),
            centroid: cand.centroid,
          }),
        );
        const aggregate = computeAggregate(parcels);
        areaAcres = Math.round(aggregate.totalArea * 100) / 100;
        totalPerimeterKm = aggregate.totalPerimeter;
        centroid = aggregate.aggregateCentroid;
      }

      const result: BoundaryResult = {
        boundary: top.coordinates,
        areaAcres,
        perimeterKm: totalPerimeterKm,
        centroid,
        elevationMin: elev?.min ?? null,
        elevationMax: elev?.max ?? null,
        elevationAvg: elev?.avg ?? null,
        elevationStdev: elev?.stdev ?? null,
        elevationPointsSampled: elev?.pointsSampled,
        // DC-3 — propagate the cascade audit trail so the
        // downstream createSite path can persist it onto
        // siteElevationRange (same channel refreshSiteElevation
        // uses; siteEvidence stamps `attemptStages` from it).
        ...(cascadeSource ? { cascadeSource } : {}),
        ...(cascadeStages && cascadeStages.length > 0
          ? { cascadeStages }
          : {}),
        parcels,
      };
      setBoundaryResult(result);
      setParsedKmz(parsed);
      setLayersReviewed(false);
      setParsedSiteLayers([]);
      // AC2 — KMZ-imported centroids are canonical too. Reverse-geocode the
      // imported centroid and surface drift vs. typed search if any.
      void revGeocodeAndMaybeWarn(result.centroid, selectedPlace);
      toast.success(`Imported "${parsed.fileName}" — ${Math.round(top.areaAcres)} acres, ${parsed.layers.length} additional features`);
    } catch (e) {
      toast.error(`KMZ parse failed: ${(e as Error).message}`);
    } finally {
      setIsImportingKmz(false);
    }
  };

  // NOTE: the KMZ layer-review step (`LayerReviewPanel` + its import/skip
  // handlers) was retired with the create-site wizard. A KMZ imported in the
  // modal contributes its boundary only; any extra layers can be reviewed +
  // imported on the site's Map tab post-create. `parsedSiteLayers` is still
  // threaded into the create payload (empty unless a future caller seeds it).

  const [showDataSources, setShowDataSources] = useState(false);

  // DRA-4 — SiteRagEngine sidebar collapse.
  // The sidebar is fixed/overlay below md (≥ md it's part of the static
  // flex layout). `sidebarOpenMobile` drives a CSS transform toggle plus
  // a backdrop overlay. The matchMedia listener resets the flag whenever
  // the viewport crosses the md breakpoint so the sidebar can't be
  // "stuck open" if the user resizes their browser past md.
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(min-width: 768px)');
    const handle = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) setSidebarOpenMobile(false);
    };
    // Sync once at mount, then subscribe.
    handle(mql);
    // Safari < 14 lacks addEventListener on MediaQueryList; fall back to
    // the deprecated addListener with the same signature.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handle);
      return () => mql.removeEventListener('change', handle);
    }
    mql.addListener(handle);
    return () => mql.removeListener(handle);
  }, []);

  useEffect(() => {
    if (!inputRef || !isApiLoaded) return;

    // TODO(maps-migration): Replace google.maps.places.Autocomplete with
    // PlaceAutocompleteElement — see https://developers.google.com/maps/documentation/javascript/places-migration-overview
    const autocomplete = new google.maps.places.Autocomplete(inputRef, {
      fields: ['geometry', 'name', 'formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      setSelectedPlace(place);
      setSearchQuery(place.name || place.formatted_address || '');
      if (place.name && !siteNameTouchedRef.current) setSiteName(place.name);
    });

    return () => {
      google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [inputRef, isApiLoaded]);

  return (
    <PortfolioSiteListContext.Provider value={siteListContext}>
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-background font-mono overflow-hidden">
      {/* Header */}
      {/* Header removed — navigation now in App.tsx main menu */}

      {/* Data Sources Panel */}
      {showDataSources && (
        <div className="border-b border-border bg-state-hover px-6 py-4 shrink-0 overflow-x-auto">
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-text-secondary" />
            <span className="type-section text-text-secondary">Integrated Data Sources</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
            {PROVIDER_CATALOG.map(p => {
              const hasKey = p.keyField ? !!userApiKeys[p.keyField] : true;
              const status: 'active' | 'key-required' | 'paid' = !p.requiresKey ? 'active' : hasKey ? 'active' : ((p.pricing ?? '').startsWith('From') ? 'paid' : 'key-required');
              return (
                <DataSourceCard
                  key={p.id}
                  icon={<Database className="w-3.5 h-3.5" />}
                  name={p.name}
                  description={p.description}
                  fields={p.fields}
                  status={status}
                  url={p.signupUrl || '#'}
                  pricing={p.pricing}
                />
              );
            })}
            {/* Always-on: Google Maps */}
            <DataSourceCard
              icon={<MapPin className="w-3.5 h-3.5" />}
              name="Google Maps"
              description="Elevation, Places, Satellite"
              fields="Elevation, Geocoding"
              status="active"
              url="https://developers.google.com/maps"
              pricing="Free (platform key)"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-border-subtle">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-success" />
              <span className="type-meta">Active</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-warning" />
              <span className="type-meta">Free Key Required</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <span className="type-meta">Paid Subscription</span>
            </div>
            <div className="flex-1" />
            <span className="type-meta">Cached in Firestore (1-30 day TTL). All data cited in report appendix.</span>
          </div>
        </div>
      )}

      <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden md:flex-row">
        {/* Keep the mobile sites toggle in its own row so it cannot cover the
            site's tabs or report controls. The desktop sidebar stays static. */}
        <div className="md:hidden shrink-0 px-2 py-2" data-testid="siterag-mobile-sites-row">
          <button
            type="button"
            className="relative z-40 min-h-11 min-w-11 inline-flex items-center justify-center rounded-md bg-background/80 border border-border shadow-sm backdrop-blur-sm"
            onClick={() => setSidebarOpenMobile(v => !v)}
            aria-label="Toggle sites list"
            aria-expanded={sidebarOpenMobile}
            data-testid="siterag-sidebar-toggle"
          >
            <MenuIcon className="w-4 h-4 text-text-secondary" />
          </button>
        </div>

        {/* DRA-4 — Backdrop. Only renders on mobile when the sidebar
            is open; tapping it dismisses. The md:hidden guard guarantees
            the backdrop disappears at md+ even if state desyncs. */}
        {sidebarOpenMobile && (
          <div
            className="md:hidden fixed inset-0 z-20 bg-background/60 backdrop-blur-xs"
            onClick={() => setSidebarOpenMobile(false)}
            aria-hidden="true"
            data-testid="siterag-sidebar-backdrop"
          />
        )}

        {/* Sidebar - History & New Report */}
        <aside
          className={cn(
            "border-r border-border-subtle bg-rail flex flex-col shrink-0",
            "fixed inset-y-0 left-0 z-30 w-80 transform transition-transform duration-200",
            "md:static md:translate-x-0 md:w-80",
            sidebarOpenMobile ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}
          data-testid="siterag-sidebar"
        >
            <div className="p-4 space-y-4">
              {/* Aurora Cockpit — accent primary CTA with the cockpit glow
                  (mirrors .da-btn--primary). Full-width, mono uppercase. */}
              <button
                type="button"
                className="da-btn da-btn--primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => { void openWizard(); }}
                disabled={siteRag.isLoading}
              >
                <Plus className="w-3.5 h-3.5" /> New Site
              </button>

              {/* A genuine generation failure surfaces the red banner. Provider
                  precedence is detect-and-record-only now (recorded to the audit
                  section, never an error), so it never reaches siteRag.error. */}
              {siteRag.error && (
                <div className="p-3 border border-danger/40 bg-danger/15 space-y-1">
                  <div className="type-body font-bold text-danger uppercase tracking-widest flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" /> Generation Failed
                  </div>
                  <p className="type-meta text-danger leading-relaxed">{siteRag.error}</p>
                  <GenerationErrorSourceLink error={siteRag.error} />
                  <Button variant="ghost" size="sm" className="h-6 type-meta text-danger"
                    onClick={() => useStore.getState().clearGenerationError()}
                  >Dismiss</Button>
                </div>
              )}
              {/* Public Sentiment held for site evidence (a build in progress, or
                  evidence that needs a rebuild) is NOT a failure: it gets its own
                  notice with Cancel / Rebuild SED instead of "Generation Failed". */}
              <SentimentEvidenceSidebarNotices />
            </div>

            <div className="flex-1 overflow-y-auto border-t border-border-subtle">
              <div className="p-4 space-y-2">
                {/* Aurora Cockpit — SITES(n) eyebrow with the canonical
                    glow-dot (.da-tabhead__dot recipe). */}
                <div className="da-tabhead__eyebrow mb-2">
                  <span className="da-tabhead__dot" aria-hidden />
                  {/* Gate the count on isSitesLoaded so we never flash "(0)" before
                      fetchSites resolves. Dashboard reads this on first render. */}
                  {siteRag.isSitesLoaded ? (
                    <span className="da-type-eyebrow">
                      Sites <span className="text-text-primary">({siteRag.sites.length})</span>
                    </span>
                  ) : (
                    <span className="da-type-eyebrow text-text-muted">Loading sites…</span>
                  )}
                </div>

                {/* Search & Filter */}
                {siteRag.sites.length > 1 && (
                  <div className="space-y-1.5 pb-2">
                    {/* Aurora Cockpit — search with a leading icon affix and an
                        accent focus ring (mirrors round2 command-rail). */}
                    <div className="flex gap-1 items-start">
                      <div className="flex gap-1 flex-wrap">
                        {/* Org / sub-org scope — only meaningful when the
                            user's tree actually has sub-orgs (Part B). For a
                            single company with no children we hide this row
                            entirely and lean on the verdict filter below.
                            Suppressed when the hierarchy-aware
                            DashboardDataScopeSelector (showDataScope) renders
                            below, so a parent-org user sees exactly ONE
                            company-axis control — these legacy "All companies +
                            companyCards" chips remain only for cross-estate
                            views where the scope selector is hidden (e.g.
                            admins spanning unrelated orgs). */}
                        {showCompanyFilter && !showDataScope && (
                          <>
                            <button
                              type="button"
                              data-testid="company-filter-all"
                              aria-pressed={companyFilterId === 'all'}
                              className={`type-meta px-2 py-0.5 rounded-full uppercase tracking-widest border transition-colors ${companyFilterId === 'all' ? 'selected-accent' : 'border-border text-text-muted hover:text-text-secondary hover:border-border-strong'}`}
                              onClick={() => setCompanyFilterId('all')}
                            >
                              All companies
                            </button>
                            {companyCards.map((company) => (
                              <button
                                key={company.id}
                                type="button"
                                data-testid={`company-filter-${company.id}`}
                                aria-pressed={companyFilterId === company.id}
                                className={`type-meta px-2 py-0.5 rounded-full uppercase tracking-widest border transition-colors ${companyFilterId === company.id ? 'selected-accent' : 'border-border text-text-muted hover:text-text-secondary hover:border-border-strong'}`}
                                onClick={() => setCompanyFilterId(company.id)}
                                title={`Filter to ${company.name} and child companies`}
                              >
                                {company.name}
                              </button>
                            ))}
                          </>
                        )}
                        {/* Verdict / status quick-filter — the useful report
                            filter to surface when there's no org scope. Only
                            renders chips for statuses present in the list. */}
                        {availableVerdictColors.length > 1 && (
                          <div className="flex gap-1 flex-wrap" data-testid="verdict-filter">
                            <button
                              type="button"
                              data-testid="verdict-filter-all"
                              aria-pressed={verdictFilter === 'all'}
                              className={`type-meta px-2 py-0.5 rounded-full uppercase tracking-widest border transition-colors ${verdictFilter === 'all' ? 'selected-accent' : 'border-border text-text-muted hover:text-text-secondary hover:border-border-strong'}`}
                              onClick={() => setVerdictFilter('all')}
                            >
                              All statuses
                            </button>
                            {availableVerdictColors.map((color) => (
                              <button
                                key={color}
                                type="button"
                                data-testid={`verdict-filter-${color}`}
                                aria-pressed={verdictFilter === color}
                                className={`type-meta px-2 py-0.5 rounded-full uppercase tracking-widest border transition-colors inline-flex items-center gap-1 ${verdictFilter === color ? 'selected-accent' : 'border-border text-text-muted hover:text-text-secondary hover:border-border-strong'}`}
                                onClick={() => setVerdictFilter(color)}
                                title={`Filter to ${VERDICT_FILTER_LABELS[color]} sites`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${bandColorClass(color)}`} aria-hidden />
                                {VERDICT_FILTER_LABELS[color]}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* #122 — data-scope toggle (My company / All
                            companies / per sub-company). Rendered ONLY for
                            users whose org actually has sub-companies; the
                            selector self-hides on an empty list. Narrows the
                            COMPANY axis of both the site cards and the report
                            list — a pure client-side subset of the already
                            authorized subtree. */}
                        {showDataScope && (
                          <DashboardDataScopeSelector
                            value={dataScope}
                            onChange={handleDataScopeChange}
                            subCompanies={dataScopeSubCompanies}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <PortfolioSiteListControls compact />

                {/* Site cards */}
                {(() => {
                  return selectedSiteList.map(siteItem => {
                    const site = siteItem.site;
                    const derivedVerdict = verdictBySiteId.get(site.id) ?? null;

                    const totalAcres = siteTotalAcres(site);
                    // HOTFIX(lat-crash) — a partial / orphan stub doc (e.g. left
                    // by a cancelled report generation) has no name and no
                    // location / centroid / boundary, so it otherwise renders as
                    // a BLANK card. Surface a fallback title + an "incomplete
                    // record" note so it reads as a recoverable stub, not a void.
                    const cardHasLocation = siteHasUsableLocation(site);
                    const cardTitle = site.name?.trim() || 'Untitled site';


                    // ATLAS — single mono stat line: `{power} · {N} ph · {acres}`.
                    // Each segment is OMITTED when absent so the line never
                    // shows a placeholder dash. Power: only when the intent
                    // carries a real bucket/exact value (not 'undecided').
                    // Phases: only for genuinely phased intents (effectivePhases
                    // synthesises a single phase for legacy intents — `isPhased`
                    // distinguishes). Acres: only when known.
                    const powerView = effectivePower(site.projectIntent);
                    const phaseView = effectivePhases(site.projectIntent);
                    const hasPowerStat = powerView.isExact || powerView.bucket !== 'undecided';
                    const hasPhaseStat = phaseView.isPhased;
                    // Leading segments (power, phases) rendered as plain text;
                    // the acres segment carries its own testid so the existing
                    // `site-card-acres-{id}` contract resolves on the stat line.
                    const leadingStatSegments: string[] = [];
                    if (hasPowerStat) leadingStatSegments.push(powerView.displayLabel);
                    if (hasPhaseStat) leadingStatSegments.push(`${phaseView.phaseCount} ph`);
                    const hasAnyStat = leadingStatSegments.length > 0 || totalAcres != null;
                    // Verdict-tinted hero glow/overlay. Null (neutral) bands
                    // render no tint — a clean grey strip.

                    // SITE-CARD-LOGOS — resolve the same audience-aware
                    // branding the report covers + report cards use. The
                    // list card shows the CUSTOMER mark only for customer
                    // sites; otherwise the COMPANY (theme) mark. Passing the
                    // ASSIGNED template makes the card brand (company name +
                    // logo) follow the template, not just the base theme.
                    //
                    // OWNING-COMPANY BRAND (2026-06-10) — the list card is an
                    // ownership mark, not a template preview. The primary
                    // mark is the OWNING company's brand
                    // (site.companyId → companies/{cid}/settings/branding +
                    // companies/{cid}.name, threaded via
                    // `owningCompanyBranding`), for EVERY viewer — a
                    // Clayco-owned site wears the Clayco mark. When that
                    // brand is unavailable (personal site / unset / read
                    // rules-denied), resolveSiteCardBranding falls back to
                    // the v1.3.2 ownership guard: own/personal sites keep
                    // the session template → theme → uploaded → default
                    // chain; cross-company sites suppress the session mark.
                    const cardTemplate = resolveActiveReportTemplate({
                      reportTemplateId: null,
                      templates: reportTemplates,
                      activeTemplateId,
                      templateOverride: draftTemplate,
                      archetype: site.archetypeOverride ?? (companyArchetype as CompanyArchetype | undefined) ?? null,
                    });
                    const cardBranding = resolveSiteCardBranding({ site, theme: effectiveTheme, template: cardTemplate, effectiveLogoUrl: cardEffectiveLogoUrl, viewerCompanyId: userCompanyId ?? null, viewerCompanyName: companyName, owningCompanyBranding: site.companyId ? owningCompanyBrandingById[site.companyId] ?? null : null });

                    const isActive = siteRag.activeSiteId === site.id;

                    return (
                    // ── ATLAS site card — parcel boundary is the hero. ──
                    // Sidebar layout (reverted from the hero-strip variant 2026-06-10
                    // per William): the parcel boundary thumbnail floats to the right;
                    // identity (name + verdict dot, owning-company brand), address, and
                    // a single mono stat line wrap to its left. Every site card
                    // opens the site's Home.
                    <div
                      key={site.id}
                      data-site-card={site.id}
                      className={`p-3 rounded-md border cursor-pointer transition-all group relative ${
                        isActive
                          ? 'bg-state-hover border-primary/40 shadow-card'
                          : 'bg-surface-1 border-border-subtle hover:bg-state-hover shadow-card'
                      }`}
                      onClick={() => openSiteOverview(site.id)}
                    >
                      {/* ── SIDEBAR LAYOUT — parcel boundary thumbnail floats to
                          the right; identity + stat line wrap to its left. ── */}
                      <div
                        className="overflow-hidden min-h-[4.25rem]"
                        data-site-card-text
                        data-testid={`site-card-thumb-${site.id}`}
                      >
                        <div className={`transition-all duration-300 ease-in-out ${isActive ? 'opacity-0 max-w-0 max-h-0 overflow-hidden m-0' : 'opacity-100 max-h-24'}`}>
                          <SiteBoundaryThumbnail site={site} layout="sidebar" testId={`site-card-thumb-svg-${site.id}`} />
                        </div>

                        {/* Row 1 — site name + verdict dot. */}
                        <div className="flex items-start justify-between mb-1 gap-1 pr-5">
                          <span
                            className={`type-section tracking-tight break-normal line-clamp-2 min-w-0 ${
                              site.name?.trim() ? 'text-text-primary' : 'text-text-muted italic'
                            }`}
                            title={cardTitle}
                          >
                            {cardTitle}
                          </span>
                          <VerdictDot
                            verdict={derivedVerdict}
                            size="sm"
                            data-testid={`dashboard-site-verdict-dot-${site.id}`}
                          />
                        </div>

                        {/* Owning-company brand mark (own row, under the title). */}
                        <SiteCardLogo
                          branding={cardBranding}
                          testIdSuffix={site.id}
                          className="mb-1"
                        />

                        {/* Address. */}
                        <div className="type-meta text-text-muted break-words">
                          {displayAddress(site)}
                        </div>

                        {/* HOTFIX(lat-crash) — location-less stub: a subtle
                            note so the card reads as an incomplete/interrupted
                            record rather than a blank tile. Opening it lands on
                            the graceful Overview "no location yet" panel. */}
                        {!cardHasLocation && (
                          <div
                            className="type-meta mt-1 inline-flex items-center gap-1 text-text-muted"
                            data-testid={`site-card-incomplete-${site.id}`}
                            title="This site has no location yet — it may be an incomplete or interrupted record."
                          >
                            <MapPin className="w-3 h-3 shrink-0" aria-hidden />
                            Location not set · incomplete record
                          </div>
                        )}

                        {/* Row 3 — single mono stat line: `{power} · {N} ph · {acres}`.
                            Present segments only; the whole line is omitted when
                            empty. The acres segment carries the existing
                            `site-card-acres-{id}` testid so that contract still
                            resolves on the stat line. */}
                        {hasAnyStat && (
                          <div
                            className="type-meta font-mono text-text-secondary mt-1.5 truncate"
                            data-testid={`site-card-stats-${site.id}`}
                          >
                            {leadingStatSegments.join(' · ')}
                            {totalAcres != null && (
                              <>
                                {leadingStatSegments.length > 0 && ' · '}
                                <span data-testid={`site-card-acres-${site.id}`}>
                                  {formatSiteAcres(totalAcres)}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                        {siteItem.recommendedCapacityLabel && <p className="pp-card-recommendation">{siteItem.recommendationCurrency === 'saved' ? 'Saved screening recommendation' : 'Screening recommendation'}: {siteItem.recommendedCapacityLabel}</p>}

                      </div>

                      {/* Hover actions — cascade-aware delete. (The Path to GO
                          launch icon is retired with the tab.) */}
                      <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button
                          className="p-1 text-danger hover:text-danger transition-colors bg-card/70 rounded backdrop-blur-sm"
                          title="Delete site"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSiteToDelete(site);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Morphing active site boundary */}
                      <div
                        className={`grid transition-all duration-500 ease-in-out ${
                          isActive ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 mt-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <SiteSidebarActiveBoundary site={site} isActive={isActive} />
                        </div>
                      </div>
                    </div>
                  );
                  });
                })()}
                {siteRag.isSitesLoaded && filteredSidebarSites.length === 0 && siteRag.sites.length > 0 && <div className="portfolio-view-empty"><p>No sites match these filters.</p><button type="button" onClick={siteListContext.onReset}>Clear search and filters</button></div>}

                {/* Empty state only shows AFTER the initial sites fetch resolves —
                    otherwise a fresh load briefly renders "No sites created yet"
                    before the data arrives. */}
                {siteRag.isSitesLoaded && siteRag.sites.length === 0 && !siteRag.isLoading && (
                  <div className="text-center py-12 space-y-2">
                    <MapPin className="w-8 h-8 text-text-disabled mx-auto" />
                    <p className="type-body text-text-muted uppercase tracking-widest">
                      No sites created yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </aside>

        {/* Main Content Area */}
        <main
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-background/30',
            // Dashboard scrolls in this <main>; block-end clearance keeps the
            // floating utility dock off the final row without sacrificing
            // horizontal content space.
            // Site views scroll inside SiteLandingPage, which carries its own
            // clearance — p-0 here so no double padding.
            engineView === 'dashboard' ? 'p-3 sm:p-4 lg:p-6 pb-overlay-chrome' : 'p-0',
          )}
        >
          <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
            {engineRenderView === 'dashboard' && (
              useDashboardV2 ? (
                <DashboardPage visibleSiteIds={dashboardVisibleSiteIds} />
              ) : (
                <SiteRagDashboard
                  onNavigateToSite={(siteId, tab) => {
                    // Plumb the dashboard's "open at Documents" CTA into the
                    // existing SiteLandingPage initialTab mechanism that the
                    // wizard already uses for fresh-site landing.
                    if (tab === 'documents' || tab === 'reports') {
                      setLandingInitialTab(tab);
                    }
                    setActiveSite(siteId);
                    setEngineView('site');
                  }}
                />
              )
            )}
            {/*
             * Task #61 — render the site landing for the normal 'site' view
             * AND for the transient `engineView === 'report'` window where no
             * report is resolved yet (e.g. clicking a site card while a
             * report was open). That in-`report`-view-without-a-report state
             * is always a transient the derivation effect (~line 785) /
             * useUrlSiteHydration is about to settle back to 'site'; showing
             * the live site landing through it prevents the
             * "Select or Generate a Report" placeholder from flashing.
             */}
            {engineRenderView === 'site-landing' && (() => {
              if (!activeSite) return null;
              return (
                <SiteLandingPage
                  // Remount per site so `initialTab` (set by the wizard for fresh
                  // sites) is consumed by useState's initialiser on every site change.
                  key={activeSite.id}
                  // SiteRagEngine still tracks 'documents' as a first-time-user
                  // landing target. SiteLandingPage's `initialTab` prop no
                  // longer accepts that literal (it's now Reports → Library),
                  // but its runtime migration shim catches the legacy string
                  // and routes to the right sub-tab. Cast through unknown so
                  // the cast is intentional and grep-able.
                  initialTab={(landingInitialTab ?? undefined) as unknown as 'home' | 'reports' | 'simulations' | 'financial' | 'supplychain' | undefined}
                  site={activeSite}
                  onViewReport={handleViewReport}
                  onGenerateReport={handleGenerateReport}
                  onGenerateTypedReport={handleGenerateTypedReport}
                  onSiteDeleted={handleSiteDeleted}
                />
              );
            })()}
            {engineRenderView === 'report' && activeReport && (window.location.pathname.endsWith('/summary') ? (
              reportPresentationMode === 'continuous-html' ? (
                <ScreeningSummaryView report={activeReport} />
              ) : (
                <ReportReaderRecovery
                  reportId={activeReport.id}
                  siteId={activeReport.siteId ?? activeSite?.id ?? null}
                />
              )
            ) : (
              <ThemeOverrideProvider value={effectiveTheme}>
                {draftTemplate != null ? (
                  <TemplateOverrideProvider value={draftTemplate}>
                    <div data-theme-provider="report" data-template-provider="studio-draft">
                      {reportPresentationMode === 'continuous-html' ? (
                        <SiteRagReportView
                          key={`${activeReport.id}:${activeReport.analysisRevision?.analysisId ?? activeReport.reportRevision?.revisionId ?? 'legacy'}`}
                          report={activeReport}
                        />
                      ) : (
                        <ReportReaderRecovery
                          reportId={activeReport.id}
                          siteId={activeReport.siteId ?? activeSite?.id ?? null}
                        />
                      )}
                    </div>
                  </TemplateOverrideProvider>
                ) : (
                  <div data-theme-provider="report" data-template-provider="store">
                    {reportPresentationMode === 'continuous-html' ? (
                      <SiteRagReportView
                        key={`${activeReport.id}:${activeReport.analysisRevision?.analysisId ?? activeReport.reportRevision?.revisionId ?? 'legacy'}`}
                        report={activeReport}
                      />
                    ) : (
                      <ReportReaderRecovery
                        reportId={activeReport.id}
                        siteId={activeReport.siteId ?? activeSite?.id ?? null}
                      />
                    )}
                  </div>
                )}
              </ThemeOverrideProvider>
            ))}
            {/* Layout view removed — integrated into simulation */}

            {/*
             * Terminal "no report, no site" placeholder. Task #61: this now
             * ONLY renders when there is genuinely no active site to fall
             * back to (the site-landing branch above handles the transient
             * report→site switch where a site IS active). In practice the
             * derivation effect resolves a no-site state to 'dashboard', so
             * this is a defensive terminal state rather than a hot path — it
             * no longer flashes during in-app site switches.
             */}
            {engineRenderView === 'empty-report-placeholder' && (() => {
              const urlTarget = parseSiteAppPath(typeof window !== 'undefined' ? window.location.pathname : '');
              if (urlTarget.siteId && urlTarget.reportId && !urlTarget.isRerun) {
                return (
                  <ReportReaderRecovery
                    reportId={urlTarget.reportId}
                    siteId={urlTarget.siteId}
                  />
                );
              }
              return (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-6 max-w-md mx-auto">
                <div className="w-20 h-20 rounded-full bg-state-hover border border-border-subtle flex items-center justify-center text-text-disabled">
                  <LayoutDashboard className="w-10 h-10" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-text-primary">Select or Generate a Report</h3>
                  <p className="type-meta leading-relaxed">
                    Use the search bar on the left to analyze a new site location or select a previous report from your history to view the full feasibility analysis.
                  </p>
                </div>
              </div>
              );
            })()}
          </div>
        </main>
      </div>

      {/* ─── CREATE-SITE MODAL (identity-only popup) ───
          2026-06-10 — replaced the full-page multi-step wizard. The popup
          captures ONLY the site's identity: a name + a location (Google
          Places autocomplete) and/or an imported KMZ boundary. Project
          intent, brief/context, and documents are now captured on the
          site's own tabs AFTER creation. Drawing a boundary on a map is
          deferred to the Map tab. Create reuses `handleCreateSiteOnly`
          (unchanged write/geocode/branding logic) and lands on Home. */}
      <Dialog
        open={showNewAnalysis}
        onOpenChange={(open, eventDetails) => {
          // The base-ui Dialog owns the focus-trap + Escape + overlay; we
          // mirror its close into our own visibility flag.
          if (open) return;
          // Google Places renders its autocomplete dropdown (`.pac-container`)
          // on document.body — OUTSIDE the dialog's portal. A click on a
          // suggestion therefore reads as an outside-press and would close
          // the modal mid-selection. Ignore an outside-press dismissal whose
          // target is inside the pac dropdown; all other closes (Escape, the
          // X button, Cancel, a genuine backdrop click) pass through.
          const reason = (eventDetails as { reason?: string } | undefined)?.reason;
          const evtTarget = (eventDetails as { event?: Event } | undefined)?.event?.target as
            | HTMLElement
            | null
            | undefined;
          if (
            reason === 'outside-press' &&
            evtTarget &&
            typeof evtTarget.closest === 'function' &&
            evtTarget.closest('.pac-container')
          ) {
            return;
          }
          handleWizardClose();
        }}
      >
        <DialogContent
          size="md"
          aria-labelledby="new-site-title"
          data-testid="new-site-modal"
        >
          <DialogHeader>
            <DialogTitle id="new-site-title" className="uppercase tracking-widest">
              New site
            </DialogTitle>
            <DialogDescription>
              Name your site and set its location. Add project intent and
              documents on the site page after it's created.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Site Name */}
            <div className="space-y-1.5">
              <Label className="type-meta font-bold flex items-center justify-between text-text-muted">
                <span>
                  <span id="site-name-label">Site name</span>
                  <span className="ml-1 text-danger" aria-hidden>*</span>
                </span>
                <span className="type-meta font-normal text-text-muted">Required</span>
              </Label>
              <Input
                placeholder="e.g. North Valley Data Center Campus"
                value={siteName}
                onChange={(e) => { setSiteName(e.target.value); siteNameTouchedRef.current = true; }}
                aria-required="true"
                aria-labelledby="site-name-label"
                data-step-autofocus
                data-testid="new-site-name-input"
                className="h-11 rounded-md border border-border bg-surface-1 text-sm focus-visible:ring-primary"
              />
            </div>

            {/* Organisation picker — only when the user's company has
                descendant sub-orgs to choose between. Defaults to the
                user's own company; selecting a descendant stamps the
                new site's companyId. Hidden (and the site assigns to
                the user's own org) when there are no sub-orgs. */}
            {showCompanyPicker && (
              <div className="space-y-1.5" data-testid="company-picker">
                <Label className="type-meta font-bold text-text-muted">
                  <span id="site-org-label">Organisation</span>
                </Label>
                <select
                  value={selectedCompanyId || userCompanyId || ''}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  aria-labelledby="site-org-label"
                  data-testid="company-picker-select"
                  className="h-11 w-full rounded-md border border-border bg-surface-1 text-sm text-text-primary focus-visible:ring-primary px-3"
                >
                  {assignableCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {assignableCompanyLabels.get(company.id) ?? company.name}
                      {company.id === userCompanyId ? ' (your org)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Location — place autocomplete OR imported KMZ boundary.
                Drawing a boundary is deferred to the Map tab post-create. */}
            <div className="space-y-1.5">
              <Label className="type-meta font-bold flex items-center justify-between text-text-muted">
                <span>
                  <span id="site-location-label">Location</span>
                  <span className="ml-1 text-danger" aria-hidden>*</span>
                </span>
                <span className="type-meta font-normal text-text-muted">Required</span>
              </Label>

              {/* Place / KMZ sibling tabs */}
              <div className="flex rounded-md border border-border-subtle bg-surface-1 overflow-hidden">
                {([['draw', 'Search address'], ['import', 'Import KMZ']] as Array<['draw' | 'import', string]>).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setKmzMode(mode)}
                    data-testid={`new-site-loc-tab-${mode}`}
                    aria-pressed={kmzMode === mode}
                    className={`flex-1 px-3 py-2 type-meta font-bold transition-colors ${
                      kmzMode === mode
                        ? 'selected-accent'
                        : 'text-text-secondary hover:bg-state-hover'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {kmzMode === 'draw' ? (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" aria-hidden />
                  <Input
                    ref={(el) => setInputRef(el)}
                    placeholder="Search address or location…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-required="true"
                    aria-labelledby="site-location-label"
                    data-testid="new-site-location-input"
                    className="pl-10 h-11 rounded-md border border-border bg-surface-1 text-sm focus-visible:ring-primary"
                  />
                </div>
              ) : (
                <KmzImportTile
                  isParsing={isImportingKmz}
                  onFile={handleKmzFile}
                />
              )}
            </div>

            {/* Resolved-location confirmation. A picked place OR an imported
                boundary satisfies the location requirement. */}
            {kmzMode === 'draw' && selectedPlace && (
              <div
                className="rounded-md border border-success/40 bg-success/10 p-3"
                data-testid="new-site-place-confirm"
              >
                <div className="type-meta font-bold text-success uppercase tracking-widest">
                  {selectedPlace.name}
                </div>
                <div className="type-meta text-text-muted">{selectedPlace.formatted_address}</div>
              </div>
            )}
            {boundaryResult && (
              <div
                className="rounded-md border border-success/40 bg-success/10 p-3 flex items-center justify-between gap-3"
                data-testid="new-site-boundary-confirm"
              >
                <div className="min-w-0">
                  <div className="type-meta font-bold text-success uppercase tracking-widest">
                    Boundary imported
                  </div>
                  <div className="type-meta text-text-muted truncate">
                    {Math.round(boundaryResult.areaAcres)} acres
                    {boundaryResult.parcels && boundaryResult.parcels.length > 1
                      ? ` · ${boundaryResult.parcels.length} parcels`
                      : ''}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 type-meta shrink-0"
                  onClick={() => {
                    setBoundaryResult(null);
                    setParsedKmz(null);
                    setParsedSiteLayers([]);
                    setLayersReviewed(false);
                  }}
                >
                  Clear
                </Button>
              </div>
            )}

            {/* Validation hint — surfaced when the user has begun but not yet
                satisfied both requirements. */}
            {!canCreateSite && (siteName.trim() !== '' || selectedPlace || boundaryResult) && (
              <p className="type-meta text-text-muted" data-testid="new-site-validation-hint">
                {siteName.trim() === ''
                  ? 'Enter a site name to continue.'
                  : 'Pick a location from search, or import a KMZ boundary, to continue.'}
              </p>
            )}
          </div>

          <DialogFooter className="mx-0 mb-0 bg-transparent border-0 p-0 pt-1">
            <Button
              variant="outline"
              className="h-10 rounded-md type-meta uppercase tracking-widest"
              onClick={handleWizardClose}
              disabled={isCreatingSite}
            >
              Cancel
            </Button>
            <Button
              className="h-10 px-6 rounded-md type-meta font-bold uppercase tracking-widest glow-band [--glow-band-color:var(--primary)]"
              onClick={async () => { await handleCreateSiteOnly(); }}
              disabled={isCreatingSite || !canCreateSite}
              data-testid="new-site-create"
            >
              {isCreatingSite ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating site…</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Create site</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site delete dialog — Workflow Prompt 7 mirrored at the site level. */}
      {siteToDelete && (
        <DeleteSiteDialog
          site={siteToDelete}
          open={true}
          onClose={() => setSiteToDelete(null)}
          onDeleted={() => { setSiteToDelete(null); }}
        />
      )}

    </div>
    </PortfolioSiteListContext.Provider>
  );
};

// Helper components

/** Drag/drop tile for KMZ uploads. Hands the File off to the parent's handler. */
const KmzImportTile = ({ isParsing, onFile }: { isParsing: boolean; onFile: (file: File) => void }) => (
  <div className="space-y-3">
    <label className="block">
      <input
        type="file"
        accept=".kmz,.kml"
        className="hidden"
        disabled={isParsing}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      <div className={`border-2 border-dashed border-border p-8 text-center cursor-pointer transition-colors ${isParsing ? 'opacity-50 pointer-events-none' : 'hover:bg-state-hover hover:border-primary/30'}`}>
        {isParsing ? (
          <div className="space-y-2">
            <Loader2 className="w-6 h-6 animate-spin text-text-muted mx-auto" />
            <span className="type-meta block font-bold">Parsing KMZ…</span>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-6 h-6 text-text-disabled mx-auto" />
            <span className="type-meta block font-bold">Drop a .kmz / .kml or click to upload</span>
            <span className="text-xs text-text-disabled block">Boundary + utilities + constraints + planned zones</span>
          </div>
        )}
      </div>
    </label>
    <p className="type-meta text-text-muted text-center leading-relaxed">
      Imports the site boundary. Extra KMZ layers (utilities, constraints,
      planned zones) can be reviewed and imported later on the site's Map tab.
    </p>
  </div>
);

// NOTE: `StepBanner` and `ResumeDraftsPanel` were retired with the
// multi-step create wizard. The identity-only modal is single-screen with
// no per-step banners and no draft-resume chooser.

/** Sum of haversine distances around a closed ring, in km. KMZ candidates ship without
 *  perimeter (the parser only computes area), so the wizard fills it in here. */
function ringPerimeterKm(coords: { lat: number; lng: number }[]): number {
  if (coords.length < 2) return 0;
  const R = 6371; // mean Earth radius, km
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    total += 2 * R * Math.asin(Math.sqrt(x));
  }
  return parseFloat(total.toFixed(2));
}

const DataSourceCard = ({ icon, name, description, fields, status, url, pricing }: {
  icon: React.ReactNode;
  name: string;
  description: string;
  fields: string;
  status: 'active' | 'key-required' | 'paid' | 'inactive';
  url: string;
  pricing?: string;
}) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="p-3 border border-border-subtle bg-background/80 hover:bg-state-hover transition-colors group space-y-2 block"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-text-secondary">
        {icon}
        <span className="type-body font-bold uppercase tracking-widest">{name}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${
          status === 'active' ? 'bg-success' :
          status === 'key-required' ? 'bg-warning' :
          status === 'paid' ? 'bg-purple-500' : 'bg-danger'
        }`} />
        {(status === 'key-required' || status === 'paid') && <Key className="w-2.5 h-2.5 text-warning" />}
      </div>
    </div>
    <p className="type-meta text-text-muted leading-relaxed">{description}</p>
    <div className="type-meta text-text-muted uppercase tracking-widest">{fields}</div>
    <div className="flex items-center justify-between">
      <span className="type-meta text-text-disabled group-hover:text-text-muted transition-colors flex items-center gap-1">
        <ExternalLink className="w-2 h-2" /> docs
      </span>
      {pricing && <span className={`type-meta font-bold ${status === 'paid' ? 'text-purple-500' : 'text-success'}`}>{pricing}</span>}
    </div>
  </a>
);

const Label = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <label className={`block ${className}`}>{children}</label>
);
